import React, { useState, useEffect, useCallback, useRef } from 'react';

import { useLocation, useNavigate } from 'react-router-dom';

import { toast } from 'react-toastify';

import { usePermissions } from '../../services/PermissionsContext';

import ShippingLinePayablesForm from '../admin/ShippingLinePayablesForm';

import OtherPayablesForm from '../admin/OtherPayablesForm';

import OfficePayablesForm from '../admin/OfficePayablesForm';

import BulkPayForm from '../admin/BulkPayForm';

import Select2Dropdown from '../common/Select2Dropdown';

import useSortableTable from '../common/useSortableTable';

import PopoutWindow from '../common/PopoutWindow';

import InvoicePreviewDialog from './InvoicePreviewDialog';

import { openFilePreview } from '../common/FilePreviewModal';

import * as XLSX from 'xlsx-js-style';



const SL_API_PAYABLES = '/api/admin/shipping-line-payables/';

const PT_API_PAYABLES = '/api/admin/other-payables/';

const OTHER_MODULE_MAP = { 'permit': 'PERMIT', 'port': 'PORT', 'documentation': 'DOCUMENTATION', 'accounts': 'ACCOUNTS' };

const PAYABLE_POPOUT_TOAST_ID = 'payable-popout-toast';

const API_TASAC_DOC = '/api/admin/shipping-line-payables/tasac-fees-document/';

const API_MY_PAYABLE_PERMS = '/api/admin/payable-user-permissions/my-permissions/';

const API_INVOICING_BASE = '/api/documentation/jobs';

const API_RECEIVABLES = '/api/admin/receivables/';



const getTasacDocumentLink = (docInfo) => {

    if (!docInfo?.file_url) return null;

    if (!docInfo?.updated_at) return docInfo.file_url;

    const separator = docInfo.file_url.includes('?') ? '&' : '?';

    return `${docInfo.file_url}${separator}v=${encodeURIComponent(docInfo.updated_at)}`;

};



const todayISO = () => new Date().toISOString().split('T')[0];



const addDays = (dateStr, days) => {

    const [y, m, d] = dateStr.split('-').map(Number);

    const date = new Date(y, m - 1, d);

    date.setDate(date.getDate() + days);

    const yyyy = date.getFullYear();

    const mm = String(date.getMonth() + 1).padStart(2, '0');

    const dd = String(date.getDate()).padStart(2, '0');

    return `${yyyy}-${mm}-${dd}`;

};



const EXPENSE_FILTER_MODES = [

    { id: 'daily', label: 'Daily' },

    { id: 'minus1d', label: '-1D' },

    { id: 'minus1w', label: '-1W' },

    { id: 'minus1m', label: '-1M' },

    { id: 'minus3m', label: '-3M' },

    { id: 'range', label: 'Date Range' },

    { id: 'search', label: 'Lookup' },

];



const Payables = ({ officeMode = false, pageTitle = 'Payables Approval', excludeOEPrefix = false }) => {

    const location = useLocation();

    const navigate = useNavigate();

    const { hasPermission, permissions } = usePermissions();

    const canManageTasacDocument = hasPermission('ACCOUNTS', 'can_create') && hasPermission('ACCOUNTS', 'can_edit');

    const [myStages, setMyStages] = useState([]);

    const [myPayablePerms, setMyPayablePerms] = useState([]); // granular per-stage permissions

    const [activeTab, setActiveTab] = useState(null);

    const [refreshTrigger, setRefreshTrigger] = useState(0);

    const [loading, setLoading] = useState(true);

    const [payables, setPayables] = useState([]);

    const [receivables, setReceivables] = useState([]);

    const [allTransporterPayables, setAllTransporterPayables] = useState([]);

    const [actionLoading, setActionLoading] = useState(false);

    

    // Popout state (multiple windows)

    const [openPayables, setOpenPayables] = useState([]);

    const [rejectReason, setRejectReason] = useState('');

    const [showRejectInput, setShowRejectInput] = useState(false);

    const [confirmDialog, setConfirmDialog] = useState({

        show: false,

        type: null,

        windowId: null,

        payable: null,

    });

    const [reverseConfirmDialog, setReverseConfirmDialog] = useState({

        show: false,

        payable: null,

    });

    const [viewingRejections, setViewingRejections] = useState(null);

    const [showTasacModal, setShowTasacModal] = useState(false);

    const [tasacDocInfo, setTasacDocInfo] = useState(null);

    const [tasacLoading, setTasacLoading] = useState(false);

    const [tasacSubmitting, setTasacSubmitting] = useState(false);

    const [tasacFile, setTasacFile] = useState(null);



    const notifyPopupError = (message) => toast.error(message, { containerId: PAYABLE_POPOUT_TOAST_ID });

    const notifyPopupSuccess = (message) => toast.success(message, { containerId: PAYABLE_POPOUT_TOAST_ID });



    // Filter & Sort State

    const [filterText, setFilterText] = useState('');

    const [filterStage, setFilterStage] = useState(''); // New: Filter by Current Stage in Rejections

    const [expenseFilter, setExpenseFilter] = useState('daily');

    const [expenseSearch, setExpenseSearch] = useState('');

    // New State for Date Range and Search Filters

    const [expenseStartDate, setExpenseStartDate] = useState('');

    const [expenseEndDate, setExpenseEndDate] = useState('');

    const [expenseSearchParam, setExpenseSearchParam] = useState(''); // Specific field (e.g. 'job_id')

    const [expenseTypeFilter, setExpenseTypeFilter] = useState('all'); // all | file | office

    

    // New: Selected Expenses for Totals Calculation

    const [selectedExpenses, setSelectedExpenses] = useState([]);

    const [selectedExpenseCharges, setSelectedExpenseCharges] = useState([]);

    // Stage 1 bulk selection state (summary only, no pay action)

    const [selectedStage1, setSelectedStage1] = useState([]);

    const [showStage1VendorDialog, setShowStage1VendorDialog] = useState(false);

    // Stage 2 bulk-pay selection state

    const [selectedStage2, setSelectedStage2] = useState([]);

    const [showStage2VendorDialog, setShowStage2VendorDialog] = useState(false);

    const [showBulkPayForm, setShowBulkPayForm] = useState(false);

    // Stage 3 bulk-approval selection state

    const [selectedStage3, setSelectedStage3] = useState([]);

    const [showStage3VendorDialog, setShowStage3VendorDialog] = useState(false);

    const [showBulkApprovalForm, setShowBulkApprovalForm] = useState(false);

    // Linked Stage 2/3 bulk group opened from a single payable row

    const [showBulkGroupForm, setShowBulkGroupForm] = useState(false);

    const [bulkGroupPayables, setBulkGroupPayables] = useState([]);

    const [showNewPayableForm, setShowNewPayableForm] = useState(false);

    const [invoice, setInvoice] = useState([]);

    const [selectedInvoice, setSelectedInvoice] = useState([]);

    const [previewOpen, setPreviewOpen] = useState(false);

    const [previewJobId, setPreviewJobId] = useState(null);

    const [previewShipmentType, setPreviewShipmentType] = useState(null);





    // Search Options for Select2Dropdown

    const expenseSearchOptions = [        

        { id: 'idf_number', text: 'IDF #' },        

        { id: 'ref_number', text: 'REF #' },        

        { id: 'bl_number', text: 'BL #' },        

        { id: 'customer_name', text: 'Customer Name' },

        { id: 'job_number', text: 'Job ID' },        

        { id: 'invoice_no', text: 'Invoice #' },

        { id: 'shipping_line_name', text: 'Vendor' },

        { id: 'container_no', text: 'Container #' }         

    ];



    const expenseSearchRef = React.useRef('');

    const expenseSearchParamRef = React.useRef('');

    // Vendor lookup type-ahead (EIR-style) — lazily loaded payee directory
    const [payeeNames, setPayeeNames] = useState(null);
    const [vendorSuggests, setVendorSuggests] = useState([]);
    const [vendorActiveIdx, setVendorActiveIdx] = useState(-1);
    const lastSelectedRef = React.useRef('');

    const selectVendorSuggestion = (name) => {
        lastSelectedRef.current = name;
        setExpenseSearch(name);
        expenseSearchRef.current = name;
        setVendorSuggests([]);
        setVendorActiveIdx(-1);
    };

    const expenseStartDateRef = React.useRef('');

    const expenseEndDateRef = React.useRef('');

    const currentFetchRef = useRef(0);

    

    useEffect(() => { 

        expenseSearchRef.current = expenseSearch; 

        expenseSearchParamRef.current = expenseSearchParam;

        expenseStartDateRef.current = expenseStartDate;

        expenseEndDateRef.current = expenseEndDate;

    }, [expenseSearch, expenseSearchParam, expenseStartDate, expenseEndDate]);

    // Lazily build the combined payee directory (vendors, transporters,
    // shipping lines, staff) the first time the Vendor lookup field is chosen.
    useEffect(() => {
        if (expenseSearchParam !== 'shipping_line_name' || payeeNames) return;
        const headers = { 'Authorization': `Bearer ${localStorage.getItem('kk_token')}` };
        const extract = (data) => (Array.isArray(data) ? data : (data.results || []));
        Promise.all([
            fetch('/api/masters/vendors/', { headers }).then(r => r.ok ? r.json() : []),
            fetch('/api/masters/transporters/', { headers }).then(r => r.ok ? r.json() : []),
            fetch('/api/masters/shippinglines/', { headers }).then(r => r.ok ? r.json() : []),
            fetch('/api/admin/staff/?active=true', { headers }).then(r => r.ok ? r.json() : []),
        ]).then(([vendors, transporters, lines, staff]) => {
            const names = new Set();
            [...extract(vendors), ...extract(transporters), ...extract(lines), ...extract(staff)].forEach(e => {
                [e.name, e.full_name].forEach(n => { if (n && n.trim()) names.add(n.trim()); });
            });
            setPayeeNames(Array.from(names).sort());
        }).catch(() => setPayeeNames([]));
    }, [expenseSearchParam, payeeNames]);

    // Customer Name is also master data — lazily load the directory once and
    // filter client-side exactly like the Vendor lookup.
    const [customerNames, setCustomerNames] = useState(null);
    useEffect(() => {
        if (expenseSearchParam !== 'customer_name' || customerNames) return;
        const headers = { 'Authorization': `Bearer ${localStorage.getItem('kk_token')}` };
        fetch('/api/masters/customers/', { headers })
            .then(r => r.ok ? r.json() : [])
            .then(data => {
                const names = new Set();
                (Array.isArray(data) ? data : (data.results || [])).forEach(e => {
                    [e.name].forEach(n => { if (n && n.trim()) names.add(n.trim()); });
                });
                setCustomerNames(Array.from(names).sort());
            })
            .catch(() => setCustomerNames([]));
    }, [expenseSearchParam, customerNames]);

    // Type-ahead suggestions for every lookup field (all trigger at 3 chars):
    //  - Vendor / Customer: client-side filter of a cached directory
    //  - IDF/BL/Job/Invoice/Container: debounced /search-options/ endpoint
    useEffect(() => {
        const term = expenseSearch.trim();
        // Suppress suggestions when the box already holds the selected value.
        if (!expenseSearchParam || term.length < 3 || term === lastSelectedRef.current) {
            setVendorSuggests([]);
            return undefined;
        }
        const t = term.toLowerCase();
        if (expenseSearchParam === 'shipping_line_name' || expenseSearchParam === 'customer_name') {
            const dir = expenseSearchParam === 'shipping_line_name' ? payeeNames : customerNames;
            if (!dir) {
                setVendorSuggests([]);
                return undefined;
            }
            setVendorSuggests(dir.filter(n => n.toLowerCase().includes(t)).slice(0, 12));
            return undefined;
        }
        const headers = { 'Authorization': `Bearer ${localStorage.getItem('kk_token')}` };
        const timeout = setTimeout(async () => {
            try {
                const res = await fetch(`${PT_API_PAYABLES}search-options/?field=${expenseSearchParam}&q=${encodeURIComponent(term)}`, { headers });
                if (!res.ok) console.error('search-options failed:', res.status);
                const data = res.ok ? await res.json() : [];
                setVendorSuggests((data || []).map(o => o.text));
            } catch {
                setVendorSuggests([]);
            }
        }, 300);
        return () => clearTimeout(timeout);
    }, [expenseSearch, expenseSearchParam, payeeNames, customerNames]);







    const allStages = [

        { id: 1, name: 'Stage 1', status: 'PENDING', nextStatus: 'APPROVED 1' },

        { id: 2, name: 'Stage 2', status: 'APPROVED 1', nextStatus: 'APPROVED 2' },

        { id: 3, name: 'Stage 3', status: 'APPROVED 2', nextStatus: 'APPROVED 3' },

        { id: 4, name: 'Expenses', status: 'APPROVED 3', nextStatus: null },

        { id: 5, name: 'Rejections', status: 'REJECTED', nextStatus: null },

        { id: 6, name: 'Pending Receipts', status: 'APPROVED 3', nextStatus: null },

        { id: 7, name: 'Uploaded Receipts', status: 'APPROVED 3', nextStatus: null }

    ];



    const [stageCounts, setStageCounts] = useState({});



    const getPayableApiUrl = (payable) => payable?._payable_source === 'shipping' ? SL_API_PAYABLES : PT_API_PAYABLES;



    // Fetch all payables linked by a Stage 2 bulk group id

    const fetchBulkGroupPayables = async (groupId) => {

        try {

            const token = localStorage.getItem('kk_token');

            const headers = { 'Authorization': `Bearer ${token}` };

            const [slRes, ptRes] = await Promise.all([

                fetch(`${SL_API_PAYABLES}by_group/?group_id=${encodeURIComponent(groupId)}`, { headers }),

                fetch(`${PT_API_PAYABLES}by_group/?group_id=${encodeURIComponent(groupId)}`, { headers }),

            ]);

            let results = [];

            if (slRes.ok) {

                const slData = await slRes.json();

                results = results.concat((Array.isArray(slData) ? slData : (slData.results || [])).map(p => ({ ...p, _payable_source: 'shipping' })));

            }

            if (ptRes.ok) {

                const ptData = await ptRes.json();

                results = results.concat((Array.isArray(ptData) ? ptData : (ptData.results || [])).map(p => ({ ...p, _payable_source: 'other' })));

            }

            results.sort((a, b) => new Date(b.created_at) - new Date(a.created_at));

            return results;

        } catch (e) {

            console.error('Failed to fetch bulk group payables', e);

            return [];

        }

    };



    // Fetch counts for current filter

    const fetchCounts = async () => {

        try {

            const token = localStorage.getItem('kk_token');

            const headers = { 'Authorization': `Bearer ${token}` };

            const ptCountsUrl = officeMode

                ? `${PT_API_PAYABLES}counts/?module=OFFICE`

                : (excludeOEPrefix ? `${PT_API_PAYABLES}counts/?exclude_reference_prefix=OE` : `${PT_API_PAYABLES}counts/`);

            const ptTransUrl = ptCountsUrl + (ptCountsUrl.includes('?') ? '&' : '?') + 'transporter=true';

            const [slRes, ptRes, ptTransRes] = await Promise.all([

                officeMode ? { ok: false } : fetch(`${SL_API_PAYABLES}counts/`, { headers }),

                fetch(ptCountsUrl, { headers }),

                fetch(ptTransUrl, { headers }),

            ]);

            const slData = slRes.ok ? await slRes.json() : {};

            const ptData = ptRes.ok ? await ptRes.json() : {};

            const ptTransData = ptTransRes.ok ? await ptTransRes.json() : {};

            const merged = {};

            new Set([...Object.keys(slData), ...Object.keys(ptData)]).forEach(k => {

                merged[k] = (slData[k] || 0) + (ptData[k] || 0);

            });

            // Transporter payables already have tax invoices, so exclude them from pending receipt counts only

            [6].forEach(stageId => {

                const k = String(stageId);

                if (merged[k] !== undefined) {

                    merged[k] = Math.max(0, merged[k] - (ptTransData[k] || 0));

                }

            });

            setStageCounts(merged);

        } catch (e) {

            console.error("Failed to fetch counts", e);

        }

    };



    useEffect(() => {

        fetchCounts();

    }, [activeTab, refreshTrigger]); // Refresh counts when tab changes or refresh is requested





    // Reset filtering and selection on tab change

    useEffect(() => {

        setSelectedExpenses([]);

        setSelectedExpenseCharges([]);

        setSelectedInvoice([]);

        setSelectedStage1([]);

        setSelectedStage2([]);

        setSelectedStage3([]); // Clear Stage 3 selection when switching tabs

        // Clear payables when on Expenses tab in search mode — no auto-fetch runs, so stale data must be removed

        if (activeTab === 4 && expenseFilter === 'search') setPayables([]);

    }, [activeTab, expenseFilter]);



    useEffect(() => {

        const defaultStage = location.state?.defaultStage;

        if (!defaultStage || myStages.length === 0) return;

        const ds = parseInt(defaultStage, 10);

        if (myStages.includes(ds)) {

            if (activeTab !== ds) {

                setActiveTab(ds);

            } else {

                setRefreshTrigger(prev => prev + 1);

            }

        }

        // Clear the state so the effect does not run again after the replace navigation

        if (navigate) {

            navigate(location.pathname, { replace: true, state: {} });

        }

    }, [location, myStages, activeTab, navigate]);



    useEffect(() => {

        const initStages = async () => {

            if (permissions && permissions.is_superuser) {

                setMyPayablePerms([]);

                setMyStages([1, 2, 3, 4, 5, 6, 7]);

                setActiveTab(1);

                setLoading(false);

            } else {

                try {

                    const token = localStorage.getItem('kk_token');

                    const res = await fetch(API_MY_PAYABLE_PERMS, {

                        headers: { 'Authorization': `Bearer ${token}` }

                    });

                    if (res.ok) {

                        const data = await res.json();

                        setMyPayablePerms(data);

                        // Show stage tab if user has view OR approve permission for that stage

                        const visibleStages = data

                            .filter(p => officeMode

                                ? (p.can_view_cash || p.can_approve_cash)

                                : (p.can_view_payables || p.can_approve_payables)

                            )

                            .map(p => p.stage);

                        setMyStages([...visibleStages, 4, 5, 6, 7]);

                        if (visibleStages.length > 0) setActiveTab(visibleStages[0]);

                        else setActiveTab(4);

                    }

                } catch (e) {

                    console.error(e);

                } finally {

                    setLoading(false);

                }

            }

        };



        if (hasPermission('PAYABLES', 'can_view')) {

            initStages();

        } else {

            setLoading(false);

        }

    }, [hasPermission, permissions]);



    const fetchTasacDocument = useCallback(async () => {

        try {

            setTasacLoading(true);

            const token = localStorage.getItem('kk_token');

            const res = await fetch(API_TASAC_DOC, {

                headers: { 'Authorization': `Bearer ${token}` },

                cache: 'no-store'

            });

            if (res.ok) {

                const data = await res.json();

                setTasacDocInfo(data?.exists ? data : null);

            }

        } catch (e) {

            console.error('Failed to fetch Tasac Fees document', e);

        } finally {

            setTasacLoading(false);

        }

    }, []);



    useEffect(() => {

        fetchTasacDocument();

    }, [fetchTasacDocument]);





    const handleTasacUpload = async () => {

        if (!tasacFile) {

            toast.error('Please choose a file to upload.');

            return;

        }



        const hadExistingDocument = !!tasacDocInfo?.file_url;



        try {

            setTasacSubmitting(true);

            const token = localStorage.getItem('kk_token');

            const payload = new FormData();

            payload.append('document_file', tasacFile);



            const res = await fetch(API_TASAC_DOC, {

                method: 'POST',

                headers: { 'Authorization': `Bearer ${token}` },

                body: payload

            });



            if (res.ok) {

                await res.json();

                await fetchTasacDocument();

                setTasacFile(null);

                toast.success(hadExistingDocument ? 'Tasac Fees document replaced successfully.' : 'Tasac Fees document uploaded successfully.');

            } else {

                const err = await res.json();

                toast.error(err?.detail || 'Failed to upload Tasac Fees document.');

            }

        } catch (e) {

            toast.error(e.message || 'Failed to upload Tasac Fees document.');

        } finally {

            setTasacSubmitting(false);

        }

    };



    const fetchPayables = useCallback(async (isBackgroundRefresh = false) => {

        setInvoice([]);

        setSelectedInvoice([]);

        if (!activeTab) return;

        const stage = allStages.find(s => s.id === activeTab);

        if (!stage) return;



        const thisFetchId = ++currentFetchRef.current;



        if (!isBackgroundRefresh) {

            setLoading(true);

        }

        try {

             // Build query string (same for both SL and PT APIs)

             let queryPath;

             if (activeTab === 5) {

                 queryPath = '?has_rejections=true';

             } else if (activeTab === 6) {

                 queryPath = '?status=APPROVED%203,CLIENT%20PAID&receipt_uploaded=false';

             } else if (activeTab === 7) {

                 queryPath = '?status=APPROVED%203,CLIENT%20PAID&receipt_uploaded=true&receipt_approved=false';

             } else if (activeTab === 4) {

                if (expenseFilter === 'search') {

                    queryPath = `?status=APPROVED%203,CLIENT%20PAID&mode=search`;

                    if (expenseSearchParamRef.current) queryPath += `&search_field=${expenseSearchParamRef.current}`;

                    if (expenseSearchRef.current) queryPath += `&search=${encodeURIComponent(expenseSearchRef.current)}`;

                } else if (expenseFilter === 'daily') {

                    queryPath = `?status=APPROVED%203,CLIENT%20PAID&mode=daily`;

                } else {

                    if (!expenseStartDateRef.current || !expenseEndDateRef.current) {

                        setPayables([]);

                        setLoading(false);

                        return;

                    }

                    queryPath = `?status=APPROVED%203,CLIENT%20PAID&mode=previous&start_date=${expenseStartDateRef.current}&end_date=${expenseEndDateRef.current}`;

                }

            } else {

                 queryPath = `?status=${stage.status || 'PENDING'}`;

             }

             queryPath += `&_t=${new Date().getTime()}`;



             const token = localStorage.getItem('kk_token');

             const headers = { 'Authorization': `Bearer ${token}` };



             const fetchFromApi = async (apiUrl, source, extraParams = '', customQueryPath = null) => {

                 const finalQueryPath = customQueryPath || queryPath;

                 const fullUrl = `${apiUrl}${finalQueryPath}${extraParams}`;

                 const res = await fetch(fullUrl, { headers });

                 if (!res.ok) return [];

                 let data = await res.json();

                 data = data.results ? data.results : data;

                 return data.map(item => ({ ...item, _payable_source: source }));

             };



             let slQueryPath = queryPath;

             let ptQueryPath = queryPath;



             // Adjust search field for each API when searching (only for Expenses tab)

             if (activeTab === 4 && expenseFilter === 'search' && expenseSearchParamRef.current) {

                 const timestamp = new Date().getTime();

                 const baseQuery = `?status=APPROVED%203,CLIENT%20PAID&mode=${expenseFilter}&search=${encodeURIComponent(expenseSearchRef.current)}&_t=${timestamp}`;



                 // Map search field for each API

                 let slSearchField = expenseSearchParamRef.current;

                 let ptSearchField = expenseSearchParamRef.current;

                 

                 // Only vendor/shipping_line_name needs different field names

                 if (expenseSearchParamRef.current === 'shipping_line_name') {

                     slSearchField = 'shipping_line_name';

                     ptSearchField = 'vendor_name';

                 }

                 

                 slQueryPath = `${baseQuery}&search_field=${slSearchField}`;

                 ptQueryPath = `${baseQuery}&search_field=${ptSearchField}`;

             }



             let data;

             if (officeMode) {

                 // Only fetch PT API with module=OFFICE filter — no SL payables in office mode

                 const officePtQueryPath = ptQueryPath + '&module=OFFICE';

                 const ptData = await fetchFromApi(PT_API_PAYABLES, 'other', '', officePtQueryPath);

                 data = ptData;

             } else {

                 // For PayablesApprovals, exclude OE-prefixed cash/office payables on all tabs except Expenses

                 const excludeSuffix = (excludeOEPrefix && activeTab !== 4) ? '&exclude_reference_prefix=OE' : '';

                 const [slData, ptData] = await Promise.all([

                     fetchFromApi(SL_API_PAYABLES, 'shipping', '', slQueryPath),

                     fetchFromApi(PT_API_PAYABLES, 'other', '', ptQueryPath + excludeSuffix),

                 ]);

                 data = [...slData, ...ptData];

             }

             data.sort((a, b) => new Date(b.created_at) - new Date(a.created_at));

             if (thisFetchId !== currentFetchRef.current) return;

            setPayables(data);

            setSelectedExpenses([]);

            setSelectedExpenseCharges([]);

             if (thisFetchId !== currentFetchRef.current) return;

             if (activeTab === 4 && expenseFilter === 'search' && ['idf_number', 'bl_number', 'invoice_no', 'ref_number', 'customer_name'].includes(expenseSearchParamRef.current) && expenseSearchRef.current) {

                try {

                    const invParams = new URLSearchParams();

                    invParams.append('customer_id', 'ALL');

                    if (expenseSearchParamRef.current === 'customer_name') {

                        invParams.append('search_field', 'customer_name');

                    }

                    invParams.append('search', expenseSearchRef.current);

                    const invRes = await fetch(`${API_INVOICING_BASE}/invoicing-generated/?${invParams.toString()}`, { headers });

                    if (invRes.ok) {

                        const invData = await invRes.json();

                        const rows = invData.rows || [];

                        const term = expenseSearchRef.current.toUpperCase().trim();

                        if (expenseSearchParamRef.current === 'customer_name') {

                            setInvoice(rows);

                            setSelectedInvoice([]);

                        } else {

                            const matches = rows.filter(r => {

                                if (expenseSearchParamRef.current === 'idf_number') return (r.idf_no || '').toUpperCase() === term;

                                if (expenseSearchParamRef.current === 'bl_number') return (r.bl || '').toUpperCase() === term || (r.bl_number || '').toUpperCase() === term;

                                if (expenseSearchParamRef.current === 'invoice_no') return (r.invoice_no || '').toUpperCase() === term;

                                if (expenseSearchParamRef.current === 'ref_number') return (r.reference_no || '').toUpperCase() === term;

                                return false;

                            });

                            setInvoice(matches.length ? matches : (rows[0] ? [rows[0]] : []));

                            setSelectedInvoice([]);

                        }

                    } else {

                        setInvoice([]);

                        setSelectedInvoice([]);

                    }

                } catch (e2) {

                    setInvoice([]);

                    setSelectedInvoice([]);

                }

            } else {

                setInvoice([]);

                setSelectedInvoice([]);

            }

        } catch (e) {

            console.error(e);

        } finally {

            if (!isBackgroundRefresh && thisFetchId === currentFetchRef.current) {

                setLoading(false);

            }

        }

    }, [activeTab, expenseFilter]);



    const fetchReceivables = useCallback(async () => {

        if (expenseFilter === 'search') return [];

        try {

            const token = localStorage.getItem('kk_token');

            const headers = { 'Authorization': `Bearer ${token}` };

            const params = new URLSearchParams();

            const dateFrom = expenseStartDateRef.current || todayISO();

            const dateTo = expenseEndDateRef.current || todayISO();

            params.set('date_from', dateFrom);

            params.set('date_to', dateTo);

            const res = await fetch(`${API_RECEIVABLES}?${params.toString()}`, { headers });

            if (!res.ok) return [];

            const data = await res.json();

            const receivablesData = Array.isArray(data) ? data : (data.results || []);

            setReceivables(receivablesData);

            return receivablesData;

        } catch (e) {

            console.error('Failed to fetch receivables', e);

            return [];

        }

    }, [expenseFilter]);



    const fetchAllTransporterPayables = useCallback(async () => {

        try {

            const token = localStorage.getItem('kk_token');

            const headers = { 'Authorization': `Bearer ${token}` };

            const url = `${PT_API_PAYABLES}?transporter=true&no_paginate=true&_t=${new Date().getTime()}`;

            const res = await fetch(url, { headers });

            if (res.ok) {

                const data = await res.json();

                setAllTransporterPayables(Array.isArray(data) ? data : (data.results || []));

            }

        } catch (e) {

            console.error('Failed to fetch all transporter payables', e);

        }

    }, []);



    // Sync date range when an expense view mode is selected

    useEffect(() => {

        if (expenseFilter === 'search') return;

        const today = todayISO();

        let startDate, endDate;

        if (expenseFilter === 'minus1d') {

            startDate = addDays(today, -1);

            endDate = today;

        } else if (expenseFilter === 'minus1w') {

            startDate = addDays(today, -6);

            endDate = today;

        } else if (expenseFilter === 'minus1m') {

            startDate = addDays(today, -29);

            endDate = today;

        } else if (expenseFilter === 'minus3m') {

            startDate = addDays(today, -89);

            endDate = today;

        } else {

            // range and daily

            startDate = today;

            endDate = today;

        }

        setExpenseStartDate(startDate);

        setExpenseEndDate(endDate);

        expenseStartDateRef.current = startDate;

        expenseEndDateRef.current = endDate;

    }, [expenseFilter]);



    useEffect(() => {

        if (activeTab === 4 && (expenseFilter === 'search' || expenseFilter === 'range')) return; // Only fetch on explicit apply/search button click

        fetchPayables();

        if (!officeMode) fetchAllTransporterPayables();

    }, [activeTab, expenseFilter, refreshTrigger]); // Auto-fetch on tab/mode/refresh change





    const handleApplyPreviousExpenses = () => {

        if (!expenseStartDate || !expenseEndDate) {

            toast.error('Please select both Start and End dates.');

            return;

        }

        expenseStartDateRef.current = expenseStartDate;

        expenseEndDateRef.current = expenseEndDate;

        fetchPayables();

    };



    // Reset filters on tab switch or permissions change

    useEffect(() => {

        setFilterStage('');

        setFilterText('');

    }, [activeTab]);



    const closePayableWindow = (windowId) => {

        setOpenPayables((prev) => prev.filter((entry) => entry.windowId !== windowId));

        setConfirmDialog((prev) => (prev.windowId === windowId ? { show: false, type: null, windowId: null, payable: null } : prev));

    };



    const handleView = async (payable) => {

        setRejectReason('');

        setShowRejectInput(false);



        // If a Stage 3 payable was approved in a linked bulk group, open the group in BulkPayForm

        if (activeTab === 3 && payable.bulk_group_id) {

            const groupPayables = await fetchBulkGroupPayables(payable.bulk_group_id);

            if (groupPayables.length > 0) {

                setBulkGroupPayables(groupPayables);

                setShowBulkGroupForm(true);

                return;

            }

        }



        let detailPayable = payable;

        try {

            const token = localStorage.getItem('kk_token');

            const res = await fetch(`${getPayableApiUrl(payable)}${payable.id}/`, {

                headers: { 'Authorization': `Bearer ${token}` }

            });

            if (res.ok) {

                const detail = await res.json();

                detailPayable = { ...payable, ...detail };

            } else {

                console.error('Failed to fetch payable details');

            }

        } catch (err) {

            console.error('Error fetching payable details', err);

        }



        const windowId = `${payable.id}_${Date.now()}`;

        setOpenPayables((prev) => [...prev, { payable: detailPayable, windowId }]);

    };



    const handleInvoicePreview = (inv) => {

        setPreviewJobId(inv.job_id);

        setPreviewShipmentType(inv.shipment_type);

        setPreviewOpen(true);

    };



    const closeInvoicePreview = () => {

        setPreviewOpen(false);

        setPreviewJobId(null);

        setPreviewShipmentType(null);

    };



    const openConfirmDialog = (type, payable, windowId) => {

        setConfirmDialog({

            show: true,

            type,

            windowId,

            payable,

        });

    };



    const closeConfirmDialog = () => {

        setConfirmDialog({ show: false, type: null, windowId: null, payable: null });

    };



    const handleApproveForPayable = async (payable, windowId) => {

        const stage = allStages.find(s => s.id === activeTab);

        if (!stage?.nextStatus) return;



        const isTransporterFinal = payable?.transporter && stage.nextStatus === 'APPROVED 3';

        const extraPayload = isTransporterFinal ? { receipt_uploaded: true, receipt_approved: true } : {};

        await updateStatus(payable, stage.nextStatus, null, true, extraPayload);

        closePayableWindow(windowId);

    };



    const handleRejectForPayable = async (payable, reasonArg, statusArg, windowId) => {

        // Handle direct call with args OR legacy state usage

        // If called from UI event, reasonArg might be event object

        let reason = rejectReason;

        let status = 'REJECTED';



        if (typeof reasonArg === 'string') {

             reason = reasonArg;

        }

        if (typeof statusArg === 'string') {

             status = statusArg;

        }



        if (!showRejectInput && typeof reasonArg !== 'string') {

            setShowRejectInput(true);

            return;

        }

        

        if (!reason || !reason.trim()) {

            notifyPopupError("Please enter a reason for rejection.");

            return;

        }

        

        await updateStatus(payable, status, reason, true);

        closePayableWindow(windowId);

    };



    const handleClientPaidForPayable = async (payable, windowId, paymentDate) => {

        setActionLoading(true);

        try {

            const token = localStorage.getItem('kk_token');

            const payload = { status: 'CLIENT PAID' };

            if (paymentDate) payload.payment_date = paymentDate;

            const res = await fetch(`${getPayableApiUrl(payable)}${payable.id}/`, {

                method: 'PATCH',

                headers: { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' },

                body: JSON.stringify(payload)

            });

            if (res.ok) {

                notifyPopupSuccess('Status updated to CLIENT PAID');

                await fetchCounts();

                await fetchPayables();

                closePayableWindow(windowId);

            } else {

                const err = await res.json();

                notifyPopupError('Failed: ' + JSON.stringify(err));

            }

        } catch (e) {

            notifyPopupError(e.message);

        } finally {

            setActionLoading(false);

        }

    };



    const handleConfirmDialogProceed = async () => {

        if (!confirmDialog?.show || !confirmDialog?.payable || !confirmDialog?.windowId) return;



        if (confirmDialog.type === 'approve') {

            await handleApproveForPayable(confirmDialog.payable, confirmDialog.windowId);

        } else if (confirmDialog.type === 'receipt_approve') {

            await handleReceiptApproveForPayable(confirmDialog.payable, confirmDialog.windowId);

        } else if (confirmDialog.type === 'client_paid') {

            await handleClientPaidForPayable(confirmDialog.payable, confirmDialog.windowId);

        }



        closeConfirmDialog();

    };



    const handleReceiptApproveForPayable = async (payable, windowId) => {

        if (!payable) return;

        setActionLoading(true);

        try {

             // Set receipt_approved=True

             const res = await fetch(`${getPayableApiUrl(payable)}${payable.id}/`, {

                 method: 'PATCH',

                 headers: { 

                     'Authorization': `Bearer ${localStorage.getItem('kk_token')}`,

                     'Content-Type': 'application/json'

                 },

                 body: JSON.stringify({ receipt_approved: true })

             });

             if (res.ok) {

                 closePayableWindow(windowId);

                 Promise.all([fetchCounts(), fetchPayables()]);

             } else {

                 const err = await res.json();

                 notifyPopupError("Failed to approve receipt: " + JSON.stringify(err));

             }

        } catch (e) {

            console.error(e);

            notifyPopupError("Error: " + e.message);

        } finally {

            setActionLoading(false);

        }

    };



    const handleReceiptRejectForPayable = async (payable, reason, replaceConfig = {}, windowId) => {

        if (!payable) return;

        if (!reason) { notifyPopupError("Rejection reason required"); return; }

        

        setActionLoading(true);

        try {

             const res = await fetch(`${getPayableApiUrl(payable)}${payable.id}/`, {

                 method: 'PATCH',

                 headers: { 

                     'Authorization': `Bearer ${localStorage.getItem('kk_token')}`,

                     'Content-Type': 'application/json'

                 },

                 body: JSON.stringify({ 

                     receipt_rejection_reason: reason,

                     receipt_uploaded: false,

                     receipt_approved: false,

                     allow_replace_tax_invoice: !!replaceConfig.allow_replace_tax_invoice,

                     allow_replace_receipt: !!replaceConfig.allow_replace_receipt,

                     // Backend handles setting approved=False and uploaded=False

                 })

             });

             if (res.ok) {

                 await fetchCounts();

                 await fetchPayables();

                 closePayableWindow(windowId);

             } else {

                 const err = await res.json();

                 notifyPopupError("Failed to reject receipt: " + JSON.stringify(err));

             }

        } catch (e) {

            console.error(e);

            notifyPopupError("Error: " + e.message);

        } finally {

            setActionLoading(false);

        }

    };



    const handleDelete = async (payable) => {

        if (!window.confirm("Are you sure you want to delete this Payable? This action cannot be undone.")) return;

        

        setActionLoading(true);

        try {

            const token = localStorage.getItem('kk_token');

            const res = await fetch(`${getPayableApiUrl(payable)}${payable.id}/`, {

                method: 'DELETE',

                headers: { 'Authorization': `Bearer ${token}` }

            });

            if (res.ok) {

                toast.success("Payable Deleted");

                await fetchCounts();

                await fetchPayables();

            } else {

                toast.error("Failed to delete");

            }

        } catch (e) {

            toast.error(e.message);

        } finally {

            setActionLoading(false);

        }

    };

    

    const updateStatus = async (payable, status, reason = null, usePopupToast = false, extraPayload = {}) => {

        setActionLoading(true);

        try {

            const token = localStorage.getItem('kk_token');

            const payload = { status, ...extraPayload };

            if (reason) payload.rejection_reason = reason;



            const res = await fetch(`${getPayableApiUrl(payable)}${payable.id}/`, {

                method: 'PATCH',

                headers: { 

                    'Authorization': `Bearer ${token}`, 

                    'Content-Type': 'application/json' 

                },

                body: JSON.stringify(payload)

            });



            if (res.ok) {

                if (usePopupToast) {

                    notifyPopupSuccess(`Status updated to ${status}`);

                } else {

                    toast.success(`Status updated to ${status}`);

                }

                await fetchCounts();

                await fetchPayables();

            } else {

                const err = await res.json();

                if (usePopupToast) {

                    notifyPopupError("Failed: " + JSON.stringify(err));

                } else {

                    toast.error("Failed: " + JSON.stringify(err));

                }

            }

        } catch (e) {

            if (usePopupToast) {

                notifyPopupError(e.message);

            } else {

                toast.error(e.message);

            }

        } finally {

            setActionLoading(false);

        }

    };



    const handleFormSuccess = () => {

        fetchCounts();

        fetchPayables();

    };



    const handleReverseToStage3 = async (payable) => {

        setActionLoading(true);

        try {

            const token = localStorage.getItem('kk_token');

            const res = await fetch(`${getPayableApiUrl(payable)}${payable.id}/`, {

                method: 'PATCH',

                headers: { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' },

                body: JSON.stringify({ status: 'APPROVED 2' })

            });

            if (res.ok) {

                toast.success('Payable sent back to Stage 3');

                await fetchCounts();

                await fetchPayables();

            } else {

                const err = await res.json();

                toast.error('Failed: ' + JSON.stringify(err));

            }

        } catch (e) {

            toast.error(e.message);

        } finally {

            setActionLoading(false);

            setReverseConfirmDialog({ show: false, payable: null });

        }

    };

    

    const handleReverseToStage1 = async (payable) => {

        setActionLoading(true);

        try {

            const token = localStorage.getItem('kk_token');

            const res = await fetch(`${getPayableApiUrl(payable)}${payable.id}/`, {

                method: 'PATCH',

                headers: { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' },

                body: JSON.stringify({ status: 'PENDING' })

            });

            if (res.ok) {

                toast.success('Payable sent back to Stage 1');

                await fetchCounts();

                await fetchPayables();

            } else {

                const err = await res.json();

                toast.error('Failed: ' + JSON.stringify(err));

            }

        } catch (e) {

            toast.error(e.message);

        } finally {

            setActionLoading(false);

            setReverseConfirmDialog({ show: false, payable: null });

        }

    };



    // Determine current stage info

    const currentStage = allStages.find(s => s.id === activeTab);

    const isStage1 = activeTab === 1; // Assuming ID 1 is Stage 1



    const getSentToStageLabel = (item) => {

        if (item.status === 'REJECTED') return 'Creator';

        if (item.status === 'PENDING') return 'Stage 1';

        if (item.status === 'APPROVED 1') return 'Stage 2';

        if (item.status === 'APPROVED 2') return 'Stage 3';

        return '-';

    };



    const getExpensePaidByLabel = (payable) => {

        if (payable?.status === 'CLIENT PAID' || payable?.status === 'CLIENT_PAID') {

            return 'CLIENT PAID';

        }



        const paidBy = (payable?.paid_by || '').toUpperCase();

        return paidBy || 'K & K';

    };



    const getPaymentMethodDisplay = (p) => {

        if (p.status === 'CLIENT PAID' || p.status === 'CLIENT_PAID') return '-';

        if (!p.bank_account || String(p.bank_account).toUpperCase() === 'CASH') return 'CASH';

        return p.bank_name || 'CASH';

    };



    const isGepgPayable = (p) => p.control_no_type === 'GEPG' && Boolean(p.control_no);



    const formatAmountDisplay = (amount, p) => {

        const text = amount.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 });

        if (isGepgPayable(p)) {

            return <span className="gepg-amount" title={`GEPG: ${p.control_no}`}>{text}</span>;

        }

        const eirIcon = p && p.transporter && p.eir_pending ? (
            <i className="fa fa-exclamation-triangle eir-hazard" title="EIR PENDING" style={{ marginRight: 4 }}></i>
        ) : null;

        return <>{eirIcon}{text}</>;

    };



    const formatDateDisplay = (value) => {

        if (!value) return '-';

        const str = String(value);

        const match = str.match(/^(\d{4})-(\d{2})-(\d{2})/);

        if (match) return `${match[3]}/${match[2]}/${match[1]}`;

        const d = new Date(str);

        if (!isNaN(d)) return d.toLocaleDateString('en-GB');

        return str;

    };



    const getSortValue = (item, key) => {

        switch (key) {

            case 'shipping_line_name':

                if (item.transporter) return item.transporter_name || item.vendor_name || '';

                if (officeMode && item.vendor_staff_type === 'STAFF') return item.staff_name || '';

                if (officeMode) return item.vendor_short_name || item.vendor_name || '';

                return item.shipping_line_name || item.vendor_short_name || item.vendor_name || '';

            case 'paid_by_label':

                return getExpensePaidByLabel(item);

            case 'sent_to_stage':

                return getSentToStageLabel(item);

            case 'rejection_reason_display':

                return activeTab === 6 ? (item.receipt_rejection_reason || '') : (item.rejection_reason || '');

            case 'history_count':

                return item.rejections?.length || 0;

            case 'total_amount':

                return parseFloat(item.total_amount || 0) * (item.currency === 'USD' ? 1 : parseFloat(item.usd_rate || 1));

            case 'net_amount':

                return parseFloat(item.net_amount || 0) * (item.currency === 'USD' ? 1 : parseFloat(item.usd_rate || 1));

            case 'created_at':

            case 'payment_date':

            case 'invoice_date': {

                const t = item[key] ? new Date(item[key]).getTime() : NaN;

                return Number.isNaN(t) ? null : t;

            }

            case 'payment_method':

                if (!item.bank_account || String(item.bank_account).toUpperCase() === 'CASH') return 'CASH';

                return item.bank_name || 'CASH';

            case 'payment_ref':

                return item.cheque_no || '';

            case 'approved_by':

                if (item.status === 'CLIENT PAID' || item.status === 'CLIENT_PAID') {

                    return item.action_by_name || '';

                }

                return item.approved_stage_3_by_name || '';

            default:

                return item[key];

        }

    };



    const filteredData = React.useMemo(() => {

        let sorted = [...payables];



        // Client-side type filter for Expenses tab

        if (activeTab === 4 && expenseTypeFilter === 'file') {

            sorted = sorted.filter(item => item.job || item.job_id);

        } else if (activeTab === 4 && expenseTypeFilter === 'office') {

            sorted = sorted.filter(item => item.reference_no && item.reference_no.toUpperCase().includes('OE'));

        } else if (activeTab === 4 && (expenseTypeFilter === 'cash' || expenseTypeFilter === 'operation' || expenseTypeFilter === 'totalcash')) {

            sorted = sorted.filter(item => {

                if (String(item.bank_account).toUpperCase() !== 'CASH') return false;

                const paidBy = (item.paid_by || '').toUpperCase();

                if (paidBy === 'CLIENT PAID' || item.status === 'CLIENT PAID' || item.status === 'CLIENT_PAID') return false;

                const hasIdf = Boolean(item.idf_number);

                if (expenseTypeFilter === 'cash') return !hasIdf;

                if (expenseTypeFilter === 'operation') return hasIdf;

                return true;

            });

        } else if (activeTab === 4 && expenseTypeFilter === 'bank') {

            sorted = sorted.filter(item => {

                const method = getPaymentMethodDisplay(item);

                return method !== 'CASH' && method !== '-';

            });

        }



        if (filterStage) {

            sorted = sorted.filter(item => {

                if (filterStage === 'Creator') return item.status === 'REJECTED';

                if (filterStage === 'Stage 1') return item.status === 'PENDING';

                if (filterStage === 'Stage 2') return item.status === 'APPROVED 1';

                if (filterStage === 'Stage 3') return item.status === 'APPROVED 2';

                return true;

            });

        }



        // Transporter payables already have tax invoices, so skip the pending receipt workflow

        if (activeTab === 6) {

            sorted = sorted.filter(item => !item.transporter);

        }



        if (filterText) {

            const lowerFilter = filterText.toLowerCase();

            sorted = sorted.filter(item => {

                return Object.values(item).some(val => 

                    String(val).toLowerCase().includes(lowerFilter)

                );

            });

        }



        return sorted;

    }, [payables, filterText, filterStage, activeTab, expenseTypeFilter]);



    const { items: sortedData, requestSort, getSortIndicator } = useSortableTable(filteredData, { key: 'created_at', direction: 'descending' }, getSortValue);



    const transporterJobFirstIndexes = React.useMemo(() => {

        const map = new Map();

        [...allTransporterPayables]

            .sort((a, b) => new Date(a.created_at) - new Date(b.created_at))

            .forEach((p) => {

                if (p.transporter && p.job) {

                    const key = `${p.transporter}-${p.job}`;

                    if (!map.has(key)) map.set(key, p.id);

                }

            });

        return map;

    }, [allTransporterPayables]);



    const isDuplicateTransporterJob = (p) => {

        if (!p.transporter || !p.job) return false;

        return transporterJobFirstIndexes.get(`${p.transporter}-${p.job}`) !== p.id;

    };



    

    const getRefCellStyle = (p, isDup) => {

        const base = { fontWeight: '600', color: '#6c757d', whiteSpace: 'nowrap' };

        if (isDup) {

            base.animation = p.module === 'OFFICE' ? 'flash-green 1.2s infinite' : 'flash-blue 1.2s infinite';

        }

        return base;

    };



    const flashStyles = `

@keyframes flash-blue { 0%, 100% { background-color: #bfdbfe; } 50% { background-color: #2563eb; } }

@keyframes flash-green { 0%, 100% { background-color: #bbf7d0; } 50% { background-color: #16a34a; } }

@keyframes eir-flash { 0%, 100% { color: #dc2626; opacity: 1; } 50% { color: #fca5a5; opacity: 0.35; } }

.eir-hazard { animation: eir-flash 1s infinite; cursor: help; }

`;



    const getVendorDisplay = (p) => {

        if (p.transporter) return p.transporter_name || p.vendor_name || '-';

        if (officeMode && p.vendor_staff_type === 'STAFF') return p.staff_name || '-';

        if (officeMode) return p.vendor_short_name || p.vendor_name || '-';

        return p.shipping_line_name || p.vendor_short_name || p.vendor_name || '-';

    };



    const getCreatedByDisplay = (p) => {

        return p.transporter ? (p.transporter_name || '-') : (p.created_by_name || '-');

    };



    const exportExpensesExcel = async () => {

        if (expenseTypeFilter !== 'bank_details' && sortedData.length === 0) {

            toast.info('There are no visible expenses to export.');

            return;

        }



        const reportTypes = {

            all: 'ALL EXPENSES',

            file: 'FILE EXPENSES',

            office: 'CASH/OFFICE EXPENSES',

            totalcash: 'TOTAL CASH EXPENSES',

            cash: 'CASH EXPENSES',

            operation: 'OPERATION EXPENSES',

            bank: 'BANK EXPENSES',

            bank_details: 'BANK DETAILS'

        };

        const formatReportDate = date => date ? new Date(`${date}T00:00:00`).toLocaleDateString('en-GB') : '';

        const reportDateRange = expenseFilter === 'previous'

            ? `${formatReportDate(expenseStartDate)} - ${formatReportDate(expenseEndDate)}`

            : expenseFilter === 'daily'

                ? new Date().toLocaleDateString('en-GB')

                : `${formatReportDate(expenseStartDate)} - ${formatReportDate(expenseEndDate)}`;

        const reportTitle = `${reportTypes[expenseTypeFilter] || 'ALL EXPENSES'} FOR ${reportDateRange}`;



        if (expenseTypeFilter === 'bank') {

            const bankPayables = [...sortedData].filter(p => {

                const method = getPaymentMethodDisplay(p);

                return method !== 'CASH' && method !== '-';

            });

            if (bankPayables.length === 0) {

                toast.info('There are no visible bank expenses to export.');

                return;

            }

            const dateKey = p => p.payment_date ? new Date(p.payment_date).getTime() : Infinity;

            bankPayables.sort((a, b) => dateKey(a) - dateKey(b));



            const bankRows = [];

            bankPayables.forEach(p => {

                const invoiceCurrencyDifferent = p.currency === 'TZS' && parseFloat(p.usd_rate || 0) > 100;

                const hasOldItems = p.items && p.items.some(item => !item.foreign_amount || item.foreign_amount === 0) && p.items.some(item => !item.total_amount);

                const amount = parseFloat(p.total_amount || 0);

                const displayedAmount = invoiceCurrencyDifferent && hasOldItems && p.usd_rate

                    ? Math.round(amount * parseFloat(p.usd_rate) * 100) / 100

                    : amount;

                const isUsd = p.currency === 'USD';

                const tsh = isUsd ? '' : displayedAmount;

                const usd = isUsd ? displayedAmount : '';

                const vendor = p.vendor_name || p.vendor_short_name || p.shipping_line_name || p.transporter_name || '';

                const bankName = getPaymentMethodDisplay(p);

                const collectedBy = p.collected_by_name || p.collected_by?.name || p.collected_by || p.paid_by || '';

                const paymentDate = formatDateDisplay(p.payment_date);

                const paymentRef = p.cheque_no || p.payment_ref || '';

                const baseRow = [

                    p.idf_number || '',

                    p.customer_short_name || p.customer_name || '',

                    vendor,

                    tsh,

                    usd,

                    bankName,

                    paymentRef,

                    collectedBy,

                    paymentDate

                ];

                bankRows.push(baseRow);

                if ((p.tiss_tips === 'TISS' || p.tiss_tips === 'TIPS') && parseFloat(p.tiss_tips_amount || 0) > 0) {

                    const suffix = p.tiss_tips;

                    const tissRef = paymentRef ? `${paymentRef}-${suffix}` : suffix;

                    bankRows.push([

                        p.idf_number || '',

                        p.customer_short_name || p.customer_name || '',

                        vendor,

                        parseFloat(p.tiss_tips_amount || 0),

                        '',

                        bankName,

                        tissRef,

                        collectedBy,

                        paymentDate

                    ]);

                }

                if (p.control_no_type === 'GEPG' && parseFloat(p.gepg_amount || 0) > 0) {

                    bankRows.push([

                        p.idf_number || '',

                        p.customer_short_name || p.customer_name || '',

                        vendor,

                        parseFloat(p.gepg_amount || 0),

                        '',

                        bankName,

                        p.control_no || paymentRef,

                        collectedBy,

                        paymentDate

                    ]);

                }

            });



            const bankTitle = `K & K COMPANY LTD. - BANK EXPENSES FROM ${reportDateRange}`;

            const bankHeaders = ['IDF NO', 'IMPORTER', 'VENDOR', 'TSH', 'USD', 'BANK NAME', 'PAYMENT REF', 'COLLECTED BY', 'PAYMENT DATE'];

            const totalTsh = bankRows.reduce((sum, row) => sum + (parseFloat(row[3]) || 0), 0);

            const totalUsd = bankRows.reduce((sum, row) => sum + (parseFloat(row[4]) || 0), 0);

            const totalRow = ['', '', 'TOTAL', totalTsh, totalUsd, '', '', '', ''];

            const worksheet = XLSX.utils.aoa_to_sheet([[bankTitle], bankHeaders, ...bankRows, totalRow]);

            const totalRowIdx = bankRows.length + 3;

            const mergeColumns = 8;

            const worksheetCols = [{ wch: 18 }, { wch: 28 }, { wch: 24 }, { wch: 16 }, { wch: 16 }, { wch: 28 }, { wch: 28 }, { wch: 24 }, { wch: 16 }];

            const lightBlueStyle = { fill: { patternType: 'solid', fgColor: { rgb: 'DDEBF7' } }, font: { bold: true } };

            const lightRedStyle = { fill: { patternType: 'solid', fgColor: { rgb: 'FFCDD2' } }, font: { bold: true } };

            worksheet['!cols'] = worksheetCols;

            worksheet['!merges'] = [{ s: { r: 0, c: 0 }, e: { r: 0, c: mergeColumns } }];

            worksheet.A1.s = { font: { bold: true, sz: 14 }, alignment: { horizontal: 'center' } };

            const headerCells = bankHeaders.map((_, i) => `${String.fromCharCode(65 + i)}2`);

            headerCells.forEach(cell => {

                if (worksheet[cell]) worksheet[cell].s = lightBlueStyle;

            });

            ['C', 'D', 'E'].forEach(col => {

                const cell = `${col}${totalRowIdx}`;

                if (worksheet[cell]) worksheet[cell].s = lightRedStyle;

            });

            ['D', 'E'].forEach(col => {

                for (let row = 3; row <= totalRowIdx; row++) {

                    const cell = worksheet[`${col}${row}`];

                    if (cell) {

                        cell.z = '#,##0.00';

                        cell.s = { ...(cell.s || {}), numFmt: '#,##0.00' };

                    }

                }

            });

            const thinBorder = {

                top: { style: 'thin', color: { rgb: '000000' } },

                bottom: { style: 'thin', color: { rgb: '000000' } },

                left: { style: 'thin', color: { rgb: '000000' } },

                right: { style: 'thin', color: { rgb: '000000' } }

            };

            Object.keys(worksheet).forEach(cell => {

                if (cell[0] === '!') return;

                worksheet[cell].s = { ...(worksheet[cell].s || {}), border: thinBorder };

            });



            const baseReportName = reportTypes.bank;

            const sheetName = baseReportName.replace(/[^a-zA-Z0-9 ]/g, '').slice(0, 31);

            const fileName = `${baseReportName.replace(/[^a-zA-Z0-9]/g, '_')}_Report_${new Date().toISOString().slice(0, 10)}.xlsx`;



            const workbook = XLSX.utils.book_new();

            XLSX.utils.book_append_sheet(workbook, worksheet, sheetName || 'Report');

            XLSX.writeFile(workbook, fileName);

            return;

        }



        if (expenseTypeFilter === 'bank_details') {

            const rangeText = expenseFilter === 'daily'

                ? new Date().toLocaleDateString('en-GB')

                : `${formatReportDate(expenseStartDate)} - ${formatReportDate(expenseEndDate)}`;



            const receivablesForExport = await fetchReceivables();



            if (sortedData.length === 0 && receivablesForExport.length === 0) {

                toast.info('There are no visible expenses or receivables to export.');

                return;

            }



            const isClientPaid = (p) => p.status === 'CLIENT PAID' || p.status === 'CLIENT_PAID' || (p.paid_by || '').toUpperCase() === 'CLIENT PAID';

            const isCash = (p) => !p.bank_account || String(p.bank_account).toUpperCase() === 'CASH';

            const getRowAmount = (p) => {

                const invoiceCurrencyDifferent = p.currency === 'TZS' && parseFloat(p.usd_rate || 0) > 100;

                const hasOldItems = p.items && p.items.some(item => !item.foreign_amount || item.foreign_amount === 0) && p.items.some(item => !item.total_amount);

                const amount = parseFloat(p.total_amount || 0);

                return invoiceCurrencyDifferent && hasOldItems && p.usd_rate

                    ? Math.round(amount * parseFloat(p.usd_rate) * 100) / 100

                    : amount;

            };

            const sortByPaymentDate = (a, b) => {

                const da = a.payment_date ? new Date(a.payment_date).getTime() : Infinity;

                const db = b.payment_date ? new Date(b.payment_date).getTime() : Infinity;

                return da - db;

            };

            const sortByReceivableDate = (a, b) => {

                const da = a.date ? new Date(a.date).getTime() : Infinity;

                const db = b.date ? new Date(b.date).getTime() : Infinity;

                return da - db;

            };



            const styleWorksheet = (worksheet, mergeColumns, headerRows, totalRows, amountColIndexes, worksheetCols, leftAlignedTitleRows = []) => {

                const lightBlueStyle = { fill: { patternType: 'solid', fgColor: { rgb: 'DDEBF7' } }, font: { bold: true } };

                const thinBorder = {

                    top: { style: 'thin', color: { rgb: '000000' } },

                    bottom: { style: 'thin', color: { rgb: '000000' } },

                    left: { style: 'thin', color: { rgb: '000000' } },

                    right: { style: 'thin', color: { rgb: '000000' } }

                };



                worksheet['!cols'] = worksheetCols;

                const merges = [{ s: { r: 0, c: 0 }, e: { r: 0, c: mergeColumns } }];

                leftAlignedTitleRows.forEach(r => {

                    const row = typeof r === 'number' ? r : r.row;

                    const cols = typeof r === 'number' ? mergeColumns : r.columns;

                    merges.push({ s: { r: row - 1, c: 0 }, e: { r: row - 1, c: cols } });

                });

                worksheet['!merges'] = merges;

                if (worksheet.A1) worksheet.A1.s = { font: { bold: true, sz: 14 }, alignment: { horizontal: 'center' } };

                headerRows.forEach(r => {

                    for (let c = 0; c <= mergeColumns; c++) {

                        const cell = worksheet[`${String.fromCharCode(65 + c)}${r}`];

                        if (cell) cell.s = lightBlueStyle;

                    }

                });

                leftAlignedTitleRows.forEach(r => {

                    const row = typeof r === 'number' ? r : r.row;

                    const cell = worksheet[`A${row}`];

                    if (cell) cell.s = { ...lightBlueStyle, font: { bold: true, sz: 14 }, alignment: { horizontal: 'center' } };

                });

                totalRows.forEach(r => {

                    for (let c = 0; c <= mergeColumns; c++) {

                        const cell = worksheet[`${String.fromCharCode(65 + c)}${r}`];

                        if (cell) cell.s = { ...(cell.s || {}), font: { bold: true } };

                    }

                });

                amountColIndexes.forEach(amountCol => {

                    const colLetter = String.fromCharCode(65 + amountCol);

                    const range = XLSX.utils.decode_range(worksheet['!ref']);

                    for (let row = 1; row <= range.e.r + 1; row++) {

                        const cell = worksheet[`${colLetter}${row}`];

                        if (cell) {

                            cell.z = '#,##0.00';

                            cell.s = { ...(cell.s || {}), numFmt: '#,##0.00' };

                        }

                    }

                });

                Object.keys(worksheet).forEach(cell => {

                    if (cell[0] === '!') return;

                    worksheet[cell].s = { ...(worksheet[cell].s || {}), border: thinBorder };

                });

            };



            const bankWorksheetCols = [{ wch: 18 }, { wch: 28 }, { wch: 24 }, { wch: 16 }, { wch: 16 }, { wch: 28 }, { wch: 24 }, { wch: 24 }, { wch: 16 }];

            const cashWorksheetCols = [{ wch: 14 }, { wch: 28 }, { wch: 36 }, { wch: 18 }, { wch: 45 }];

            const cashReceivableWorksheetCols = [{ wch: 28 }, { wch: 18 }, { wch: 24 }, { wch: 14 }, { wch: 18 }];



            const workbook = XLSX.utils.book_new();



            // Bank method worksheets

            const bankRows = sortedData.filter(p => !isCash(p));

            const bankReceivableRows = receivablesForExport.filter(r => r.mode !== 'CASH');

            const bankMethodNames = new Set(bankRows.map(p => p.bank_name || p.bank_account || 'UNKNOWN'));

            bankReceivableRows.forEach(r => bankMethodNames.add(r.bank_account_name || r.bank_account || 'UNKNOWN'));

            const bankMethods = [...bankMethodNames].sort();

            bankMethods.forEach(method => {

                const methodRows = sortedData.filter(p => !isClientPaid(p) && !isCash(p) && (p.bank_name || p.bank_account || 'UNKNOWN') === method).sort(sortByPaymentDate);

                const payableRows = [];

                const officeRows = [];

                const receivableRows = [];

                const refundRows = [];

                methodRows.forEach(p => {

                    const displayedAmount = getRowAmount(p);

                    const isUsd = p.currency === 'USD';

                    const tsh = isUsd ? '' : displayedAmount;

                    const usd = isUsd ? displayedAmount : '';

                    const isOEPrefix = (p.reference_no || '').toUpperCase().startsWith('OE');

                    const vendor = p.vendor_name || p.vendor_short_name || p.shipping_line_name || p.transporter_name || '';

                    const bankName = p.bank_name || method || '';

                    const collectedBy = p.collected_by_name || p.collected_by?.name || p.collected_by || p.paid_by || '';

                    const paymentDate = formatDateDisplay(p.payment_date);

                    const basePaymentRef = p.cheque_no || p.payment_ref || '';

                    const paymentRef = p.control_no || basePaymentRef;

                    const customerName = p.customer_short_name || p.customer_name || '';

                    const pushRow = (row) => {

                        if (isOEPrefix) officeRows.push(row); else payableRows.push(row);

                    };

                    pushRow([

                        p.idf_number || '',

                        customerName,

                        vendor,

                        tsh,

                        usd,

                        bankName,

                        paymentRef,

                        collectedBy,

                        paymentDate

                    ]);

                    if ((p.tiss_tips === 'TISS' || p.tiss_tips === 'TIPS') && parseFloat(p.tiss_tips_amount || 0) > 0) {

                        const suffix = p.tiss_tips;

                        const tissRef = basePaymentRef ? `${basePaymentRef}-${suffix}` : suffix;

                        pushRow([

                            p.idf_number || '',

                            customerName,

                            vendor,

                            parseFloat(p.tiss_tips_amount || 0),

                            '',

                            bankName,

                            tissRef,

                            collectedBy,

                            paymentDate

                        ]);

                    }

                    if (p.control_no_type === 'GEPG' && parseFloat(p.gepg_amount || 0) > 0) {

                        pushRow([

                            p.idf_number || '',

                            customerName,

                            vendor,

                            parseFloat(p.gepg_amount || 0),

                            '',

                            bankName,

                            basePaymentRef,

                            collectedBy,

                            paymentDate

                        ]);

                    }

                });

                const receivableRowsForMethod = bankReceivableRows.filter(r => (r.bank_account_name || r.bank_account || 'UNKNOWN') === method).sort(sortByReceivableDate);

                receivableRowsForMethod.forEach(r => {

                    const rawAmount = parseFloat(r.amount || 0);

                    if (r.is_refund || rawAmount < 0) {

                        refundRows.push([

                            (r.customer_name || '').toUpperCase(),

                            (r.mode || '').toUpperCase(),

                            r.reference_no || '',

                            (r.bank_account_name || method || '').toUpperCase(),

                            formatDateDisplay(r.date),

                            Math.abs(rawAmount)

                        ]);

                    } else {

                        const amount = rawAmount;

                        receivableRows.push([

                            (r.customer_name || '').toUpperCase(),

                            (r.mode || '').toUpperCase(),

                            r.reference_no || '',

                            (r.bank_account_name || method || '').toUpperCase(),

                            formatDateDisplay(r.date),

                            amount

                        ]);

                    }

                });



                const rows = [];

                const headerRows = [];

                const totalRows = [];

                const leftAlignedTitleRows = [];

                rows.push([`BANK EXPENSES (${rangeText})`]);

                rows.push([]);



                rows.push(['IDF NO', 'IMPORTER', 'VENDOR', 'TSHS', 'USD', 'BANK NAME', 'CHQ NO', 'COLLECTED BY', 'PAYMENT DATE']);

                headerRows.push(rows.length);

                rows.push(...payableRows);

                if (payableRows.length) {

                    const totalTsh = payableRows.reduce((s, r) => s + (parseFloat(r[3]) || 0), 0);

                    const totalUsd = payableRows.reduce((s, r) => s + (parseFloat(r[4]) || 0), 0);

                    rows.push(['', '', 'TOTAL', totalTsh, totalUsd, '', '', '', '']);

                    totalRows.push(rows.length);

                }

                rows.push([]);



                rows.push([`OFFICE EXPENSES (${rangeText})`, '']);

                leftAlignedTitleRows.push({ row: rows.length, columns: 8 });

                headerRows.push(rows.length);

                rows.push(['IDF NO', 'IMPORTER', 'VENDOR', 'TSHS', 'USD', 'BANK NAME', 'CHQ NO', 'COLLECTED BY', 'PAYMENT DATE']);

                headerRows.push(rows.length);

                rows.push(...officeRows);

                if (officeRows.length) {

                    const totalTsh = officeRows.reduce((s, r) => s + (parseFloat(r[3]) || 0), 0);

                    const totalUsd = officeRows.reduce((s, r) => s + (parseFloat(r[4]) || 0), 0);

                    rows.push(['', '', 'TOTAL', totalTsh, totalUsd, '', '', '', '']);

                    totalRows.push(rows.length);

                }

                rows.push([]);



                rows.push([`RECEIVABLES (${rangeText})`, '']);

                leftAlignedTitleRows.push({ row: rows.length, columns: 5 });

                headerRows.push(rows.length);

                rows.push(['CUSTOMER NAME', 'MODE', 'CHQ NO / REF NO', 'BANK NAME', 'DATE', 'AMOUNT']);

                headerRows.push(rows.length);

                rows.push(...receivableRows);

                if (receivableRows.length) {

                    const totalAmt = receivableRows.reduce((s, r) => s + (parseFloat(r[5]) || 0), 0);

                    rows.push(['', '', 'TOTAL', '', '', totalAmt]);

                    totalRows.push(rows.length);

                }



                if (refundRows.length) {

                    rows.push([]);

                    rows.push([`REFUNDS (${rangeText})`]);

                    leftAlignedTitleRows.push({ row: rows.length, columns: 5 });

                    headerRows.push(rows.length);

                    rows.push(['CUSTOMER NAME', 'MODE', 'CHQ NO / REF NO', 'BANK NAME', 'DATE', 'AMOUNT']);

                    headerRows.push(rows.length);

                    rows.push(...refundRows);

                    const totalRef = refundRows.reduce((s, r) => s + (parseFloat(r[5]) || 0), 0);

                    rows.push(['', '', 'TOTAL', '', '', totalRef]);

                    totalRows.push(rows.length);

                }



                const worksheet = XLSX.utils.aoa_to_sheet(rows);

                styleWorksheet(worksheet, 8, headerRows, totalRows, [3, 4, 5, 8], bankWorksheetCols, leftAlignedTitleRows);

                const sheetName = method.replace(/[^a-zA-Z0-9 ]/g, '').slice(0, 31) || 'Bank';

                XLSX.utils.book_append_sheet(workbook, worksheet, sheetName);

            });



            // CASH worksheet

            const cashData = sortedData.filter(p => !isClientPaid(p) && isCash(p) && !String(p.idf_number || '').trim()).sort(sortByPaymentDate);

            const cashRows = cashData.map(p => {

                const displayedAmount = getRowAmount(p);

                const isUsd = p.currency === 'USD';

                const staffName = p.staff_name || p.vendor_name || p.vendor_short_name || p.paid_by || '';

                const chargeName = p.charge_names || p.items?.map(item => item.charge_name).filter(Boolean).join(', ') || '';

                return [

                    formatDateDisplay(p.payment_date),

                    staffName,

                    chargeName,

                    isUsd ? '' : displayedAmount,

                    p.remarks || ''

                ];

            });

            const cashTotal = cashRows.reduce((s, r) => s + (parseFloat(r[3]) || 0), 0);

            const cashSheetRows = [

                [`CASH EXPENSES FROM ${rangeText}`],

                ['DATE', 'STAFF NAME', 'PURPOSE OF COLLECTION', 'TSHS', 'REMARKS'],

                ...cashRows

            ];

            if (cashRows.length) {

                cashSheetRows.push(['', '', 'TOTAL', cashTotal, '']);

            }

            const cashWorksheet = XLSX.utils.aoa_to_sheet(cashSheetRows);

            const cashHeaderRows = [2];

            const cashTotalRows = cashRows.length ? [cashSheetRows.length] : [];

            styleWorksheet(cashWorksheet, 4, cashHeaderRows, cashTotalRows, [3], cashWorksheetCols);

            XLSX.utils.book_append_sheet(workbook, cashWorksheet, 'CASH');



            // CASH RECEIVABLES worksheet

            const cashReceivableData = receivablesForExport.filter(r => r.mode === 'CASH').sort(sortByReceivableDate);

            const cashReceivableRows = [];

            const cashRefundRows = [];

            cashReceivableData.forEach(r => {

                const rawAmount = parseFloat(r.amount || 0);

                const base = [

                    (r.customer_name || '').toUpperCase(),

                    'CASH',

                    r.reference_no || '',

                    formatDateDisplay(r.date)

                ];

                if (r.is_refund || rawAmount < 0) {

                    cashRefundRows.push([...base, Math.abs(rawAmount)]);

                } else {

                    cashReceivableRows.push([...base, rawAmount]);

                }

            });

            const cashReceivableTotal = cashReceivableRows.reduce((s, r) => s + (parseFloat(r[4]) || 0), 0);

            const cashRefundTotal = cashRefundRows.reduce((s, r) => s + (parseFloat(r[4]) || 0), 0);



            const cashReceivableSheetRows = [

                [`CASH RECEIVABLES FROM ${rangeText}`],

                ['CUSTOMER NAME', 'MODE', 'VOUCHER NO', 'DATE', 'AMOUNT']

            ];

            const cashReceivableHeaderRows = [2];

            const cashReceivableTotalRows = [];

            const cashReceivableLeftTitleRows = [];



            cashReceivableSheetRows.push(...cashReceivableRows);

            if (cashReceivableRows.length) {

                cashReceivableSheetRows.push(['', '', 'TOTAL', '', cashReceivableTotal]);

                cashReceivableTotalRows.push(cashReceivableSheetRows.length);

            }



            if (cashRefundRows.length) {

                cashReceivableSheetRows.push([]);

                cashReceivableSheetRows.push([`CASH REFUNDS FROM ${rangeText}`]);

                cashReceivableLeftTitleRows.push({ row: cashReceivableSheetRows.length, columns: 4 });

                cashReceivableHeaderRows.push(cashReceivableSheetRows.length);

                cashReceivableSheetRows.push(['CUSTOMER NAME', 'MODE', 'VOUCHER NO', 'DATE', 'AMOUNT']);

                cashReceivableHeaderRows.push(cashReceivableSheetRows.length);

                cashReceivableSheetRows.push(...cashRefundRows);

                cashReceivableSheetRows.push(['', '', 'TOTAL', '', cashRefundTotal]);

                cashReceivableTotalRows.push(cashReceivableSheetRows.length);

            }



            const cashReceivableWorksheet = XLSX.utils.aoa_to_sheet(cashReceivableSheetRows);

            styleWorksheet(cashReceivableWorksheet, 4, cashReceivableHeaderRows, cashReceivableTotalRows, [4], cashReceivableWorksheetCols, cashReceivableLeftTitleRows);

            XLSX.utils.book_append_sheet(workbook, cashReceivableWorksheet, 'CASH RECEIVABLES');



            const baseReportName = reportTypes[expenseTypeFilter] || 'BANK DETAILS';

            const fileName = `${baseReportName.replace(/[^a-zA-Z0-9]/g, '_')}_Report_${new Date().toISOString().slice(0, 10)}.xlsx`;

            XLSX.writeFile(workbook, fileName);

            return;

        }



        const isCashReport = ['cash', 'operation', 'totalcash'].includes(expenseTypeFilter);

        let serialNo = 0;

        const rows = sortedData.flatMap(p => {

            const invoiceCurrencyDifferent = p.currency === 'TZS' && parseFloat(p.usd_rate || 0) > 100;

            const hasOldItems = p.items && p.items.some(item => !item.foreign_amount || item.foreign_amount === 0) && p.items.some(item => !item.total_amount);

            const amount = parseFloat(p.total_amount || 0);

            const displayedAmount = invoiceCurrencyDifferent && hasOldItems && p.usd_rate

                ? Math.round(amount * parseFloat(p.usd_rate) * 100) / 100

                : amount;

            const date = p.payment_date ? new Date(`${String(p.payment_date).slice(0, 10)}T00:00:00`).toLocaleDateString('en-GB') : '';

            const chargeName = p.charge_names || p.items?.map(item => item.charge_name).filter(Boolean).join(', ') || '';

            const staffName = p.staff_name || p.vendor_short_name || p.vendor_name || '';



            if (isCashReport) {

                let chunk;

                if (expenseTypeFilter === 'cash') {

                    const base = [0, date];
                    const receiptNo = p.tax_invoice_no || p.proforma_invoice_no || '';

                    if (p.items && p.items.length) {

                        chunk = p.items.map(item => [
                            ...base,
                            item.charge_name || '',
                            parseFloat(item.amount || 0),
                            parseFloat(item.vat_amount || 0),
                            parseFloat(item.total_amount || 0),
                            staffName,
                            receiptNo
                        ]);

                    } else {

                        const netAmount = parseFloat(p.net_amount || 0) || (displayedAmount - parseFloat(p.total_vat || 0));

                        chunk = [[...base, chargeName, netAmount, parseFloat(p.total_vat || 0), displayedAmount, staffName, receiptNo]];

                    }

                } else if (expenseTypeFilter === 'operation') {

                    const base = [0, date, p.idf_number || '', p.bl_number || '', p.customer_short_name || p.customer_name || ''];

                    if (p.items && p.items.length) {

                        chunk = p.items.map(item => [...base, item.charge_name || '', parseFloat(item.total_amount || 0), staffName, '']);

                    } else {

                        chunk = [[...base, chargeName, displayedAmount, staffName, '']];

                    }

                } else {

                    const base = [0, date, p.idf_number || '', p.bl_number || '', p.customer_short_name || p.customer_name || ''];

                    if (p.items && p.items.length) {

                        chunk = p.items.map(item => [...base, item.charge_name || '', parseFloat(item.total_amount || 0), staffName]);

                    } else {

                        chunk = [[...base, chargeName, displayedAmount, staffName]];

                    }

                }

                return chunk.map(row => {

                    serialNo += 1;

                    row[0] = serialNo;

                    return row;

                });

            }



            const base = [date, staffName, p.collected_by || ''];

            if (p.items && p.items.length) {

                return p.items.map(item => [...base, item.charge_name || '', parseFloat(item.total_amount || 0), p.remarks || '']);

            }

            return [[...base, chargeName, displayedAmount, p.remarks || '']];

        });



        let headers, totalRowData, mergeColumns, amountColumnIndexes, worksheetCols, totalLabelCol;

        if (isCashReport) {

            if (expenseTypeFilter === 'cash') {

                headers = ['SN', 'DATE', 'CHARGE NAME', 'NET AMOUNT', 'VAT', 'GROSS AMOUNT', 'VENDOR/STAFF NAME', 'RECEIPT NO.'];

                totalRowData = ['', '', 'TOTAL', '', '', '', '', ''];

                mergeColumns = 7;

                amountColumnIndexes = [3, 4, 5];

                worksheetCols = [{ wch: 6 }, { wch: 14 }, { wch: 36 }, { wch: 18 }, { wch: 14 }, { wch: 18 }, { wch: 28 }, { wch: 20 }];

                totalLabelCol = 2;

            } else if (expenseTypeFilter === 'operation') {

                headers = ['SN', 'DATE', 'IDF NO', 'BL NO', 'CUSTOMER', 'CHARGE NAME', 'AMOUNT (TZS)', 'STAFF NAME', 'SIGN'];

                totalRowData = ['', '', '', '', '', 'TOTAL', '', '', ''];

                mergeColumns = 8;

                amountColumnIndexes = [6];

                worksheetCols = [{ wch: 6 }, { wch: 14 }, { wch: 20 }, { wch: 20 }, { wch: 24 }, { wch: 36 }, { wch: 18 }, { wch: 28 }, { wch: 12 }];

                totalLabelCol = 5;

            } else {

                headers = ['SN', 'DATE', 'IDF NO', 'BL NO', 'CUSTOMER', 'CHARGE NAME', 'AMOUNT (TZS)', 'STAFF NAME'];

                totalRowData = ['', '', '', '', '', 'TOTAL', '', ''];

                mergeColumns = 7;

                amountColumnIndexes = [6];

                worksheetCols = [{ wch: 6 }, { wch: 14 }, { wch: 20 }, { wch: 20 }, { wch: 24 }, { wch: 36 }, { wch: 18 }, { wch: 28 }];

                totalLabelCol = 5;

            }

        } else {

            headers = ['DATE', 'GIVEN TO (STAFF)', 'COLLECTED BY', 'CHARGE NAME', 'AMOUNT', 'REMARKS'];

            totalRowData = ['', '', '', 'TOTAL', '', ''];

            mergeColumns = 5;

            amountColumnIndexes = [4];

            worksheetCols = [{ wch: 14 }, { wch: 28 }, { wch: 28 }, { wch: 36 }, { wch: 18 }, { wch: 45 }];

            totalLabelCol = 3;

        }



        amountColumnIndexes.forEach(idx => {

            totalRowData[idx] = rows.reduce((sum, row) => sum + (parseFloat(row[idx]) || 0), 0);

        });



        const worksheet = XLSX.utils.aoa_to_sheet([

            [reportTitle],

            headers,

            ...rows,

            totalRowData

        ]);

        const totalRow = rows.length + 3;

        const lightBlueStyle = { fill: { patternType: 'solid', fgColor: { rgb: 'DDEBF7' } }, font: { bold: true } };

        const lightRedStyle = { fill: { patternType: 'solid', fgColor: { rgb: 'FFCDD2' } }, font: { bold: true } };

        worksheet['!cols'] = worksheetCols;

        worksheet['!merges'] = [{ s: { r: 0, c: 0 }, e: { r: 0, c: mergeColumns } }];

        worksheet.A1.s = { font: { bold: true, sz: 14 }, alignment: { horizontal: 'center' } };

        const headerCells = headers.map((_, i) => `${String.fromCharCode(65 + i)}2`);

        const totalLabelCell = `${String.fromCharCode(65 + totalLabelCol)}${totalRow}`;

        const totalAmountCells = amountColumnIndexes.map(idx => `${String.fromCharCode(65 + idx)}${totalRow}`);

        headerCells.forEach(cell => {

            if (worksheet[cell]) worksheet[cell].s = lightBlueStyle;

        });

        [totalLabelCell, ...totalAmountCells].forEach(cell => {

            if (worksheet[cell]) worksheet[cell].s = lightRedStyle;

        });

        amountColumnIndexes.forEach(idx => {

            const amountColLetter = String.fromCharCode(65 + idx);

            for (let row = 3; row <= totalRow; row++) {

                const cell = worksheet[`${amountColLetter}${row}`];

                if (cell) {

                    cell.z = '#,##0.00';

                    cell.s = { ...(cell.s || {}), numFmt: '#,##0.00' };

                }

            }

        });



        const thinBorder = {

            top: { style: 'thin', color: { rgb: '000000' } },

            bottom: { style: 'thin', color: { rgb: '000000' } },

            left: { style: 'thin', color: { rgb: '000000' } },

            right: { style: 'thin', color: { rgb: '000000' } }

        };

        Object.keys(worksheet).forEach(cell => {

            if (cell[0] === '!') return;

            worksheet[cell].s = { ...(worksheet[cell].s || {}), border: thinBorder };

        });



        const baseReportName = reportTypes[expenseTypeFilter] || 'EXPENSES';

        const sheetName = baseReportName.replace(/[^a-zA-Z0-9 ]/g, '').slice(0, 31);

        const fileName = `${baseReportName.replace(/[^a-zA-Z0-9]/g, '_')}_Report_${new Date().toISOString().slice(0, 10)}.xlsx`;



        const workbook = XLSX.utils.book_new();

        XLSX.utils.book_append_sheet(workbook, worksheet, sheetName || 'Report');

        XLSX.writeFile(workbook, fileName);

    };



    const getExpenseChargeKey = (payable, chargeType) => `${payable._payable_source}-${payable.id}-${chargeType}`;



    // Calculate Totals for Selected Expenses

    const expenseTotals = React.useMemo(() => {

        if (activeTab !== 4 || (selectedExpenses.length === 0 && selectedExpenseCharges.length === 0 && selectedInvoice.length === 0)) return null;



        const selected = payables.filter(p => selectedExpenses.includes(p.id));

        const totals = {}; // Key: Currency



        selected.forEach(p => {

            const curr = p.currency || 'TZS';

            if (!totals[curr]) totals[curr] = { net: 0, vat: 0, gross: 0, count: 0 };



            const invoiceCurrencyDifferent = p.currency === 'TZS' && parseFloat(p.usd_rate || 0) > 100;

            const hasOldItems = p.items && p.items.some(item => !item.foreign_amount || item.foreign_amount === 0) && p.items.some(item => !item.total_amount);

            const multiplier = invoiceCurrencyDifferent && hasOldItems && p.usd_rate ? parseFloat(p.usd_rate) : 1;

            const gross = parseFloat(p.total_amount || 0) * multiplier;

            const vat = parseFloat(p.total_vat || 0) * multiplier;

            totals[curr].net += gross - vat;

            totals[curr].vat += vat;

            totals[curr].gross += gross;

            totals[curr].count++;

        });



        payables.forEach(p => {

            const tissTipsAmount = parseFloat(p.tiss_tips_amount || 0);

            if (selectedExpenseCharges.includes(getExpenseChargeKey(p, 'tiss-tips')) && tissTipsAmount > 0) {

                if (!totals.TZS) totals.TZS = { net: 0, vat: 0, gross: 0, count: 0 };

                totals.TZS.gross += tissTipsAmount;

                totals.TZS.count++;

            }



            const gepgAmount = parseFloat(p.gepg_amount || 0);

            if (selectedExpenseCharges.includes(getExpenseChargeKey(p, 'gepg')) && gepgAmount > 0) {

                if (!totals.TZS) totals.TZS = { net: 0, vat: 0, gross: 0, count: 0 };

                totals.TZS.gross += gepgAmount;

                totals.TZS.count++;

            }

        });



        selectedInvoice.forEach(inv => {

            const invCurrency = inv.currency || 'TZS';

            const invTotal = parseFloat(String(inv.total_amount || '0').replace(/,/g, ''));

            if (!totals[invCurrency]) totals[invCurrency] = { net: 0, vat: 0, gross: 0, count: 0 };

            totals[invCurrency].gross += invTotal;

            totals[invCurrency].net += invTotal;

            totals[invCurrency].count++;

        });



        return totals;

    }, [payables, selectedExpenses, selectedExpenseCharges, selectedInvoice, invoice, activeTab]);



    // Vendor totals for selected expense items

    const expenseVendorTotals = React.useMemo(() => {

        if (activeTab !== 4) return null;

        const selected = payables.filter(p => selectedExpenses.includes(p.id));

        if (selected.length === 0 && selectedExpenseCharges.length === 0) return null;



        const byVendor = {};



        selected.forEach(p => {

            const vendor = getVendorDisplay(p);

            if (!byVendor[vendor]) {

                byVendor[vendor] = { amountTZS: 0, amountUSD: 0, bankChargesTZS: 0, bankChargesUSD: 0, count: 0 };

            }



            const isUsd = p.currency === 'USD';

            const gross = parseFloat(p.total_amount || 0);

            if (isUsd) {

                byVendor[vendor].amountUSD += gross;

            } else {

                byVendor[vendor].amountTZS += gross;

            }

            byVendor[vendor].count++;

        });



        payables.forEach(p => {

            const vendor = getVendorDisplay(p);

            if (!byVendor[vendor]) {

                byVendor[vendor] = { amountTZS: 0, amountUSD: 0, bankChargesTZS: 0, bankChargesUSD: 0, count: 0 };

            }



            const tissTips = ((p.tiss_tips === 'TISS' || p.tiss_tips === 'TIPS') ? parseFloat(p.tiss_tips_amount || 0) : 0);

            const gepg = (p.control_no_type === 'GEPG' ? parseFloat(p.gepg_amount || 0) : 0);



            if (selectedExpenseCharges.includes(getExpenseChargeKey(p, 'tiss-tips')) && tissTips > 0) {

                byVendor[vendor].bankChargesTZS += tissTips;

            }



            if (selectedExpenseCharges.includes(getExpenseChargeKey(p, 'gepg')) && gepg > 0) {

                byVendor[vendor].bankChargesTZS += gepg;

            }

        });



        return byVendor;

    }, [payables, selectedExpenses, selectedExpenseCharges, activeTab]);



    // Summary cards for Expenses tab (similar to Receivables)

    const expenseSummary = React.useMemo(() => {

        if (activeTab !== 4) return null;

        const all = {};

        const cash = {};

        const bank = {};

        const clientPaid = {};

        const byBank = {};



        const ensure = (map, cur) => {

            if (!map[cur]) map[cur] = { net: 0, vat: 0, gross: 0, count: 0 };

            return map[cur];

        };



        filteredData.forEach(p => {

            const curr = p.currency || 'TZS';

            const multiplier = 1;

            const net = parseFloat(p.net_amount || 0) * multiplier;

            const vat = parseFloat(p.total_vat || 0) * multiplier;

            const gross = parseFloat(p.total_amount || 0) * multiplier;



            const allEntry = ensure(all, curr);

            allEntry.net += net;

            allEntry.vat += vat;

            allEntry.gross += gross;

            allEntry.count++;



            const isCash = String(p.bank_account).toUpperCase() === 'CASH';

            const isClientPaid = p.status === 'CLIENT PAID' || p.status === 'CLIENT_PAID';

            const target = isClientPaid ? clientPaid : (isCash ? cash : bank);

            const entry = ensure(target, curr);

            entry.net += net;

            entry.vat += vat;

            entry.gross += gross;

            entry.count++;



            if (!isCash && !isClientPaid) {

                const bankKey = p.bank_account_details || p.bank_name || 'UNKNOWN';

                if (!byBank[bankKey]) byBank[bankKey] = {};

                byBank[bankKey][curr] = (byBank[bankKey][curr] || 0) + gross;

            }



            // TISS/TIPS and GEPG charges are always TZS

            const tissTipsAmount = parseFloat(p.tiss_tips_amount || 0);

            if ((p.tiss_tips === 'TISS' || p.tiss_tips === 'TIPS') && tissTipsAmount > 0) {

                ensure(all, 'TZS').gross += tissTipsAmount;

                if (isClientPaid) {

                    ensure(clientPaid, 'TZS').gross += tissTipsAmount;

                } else {

                    ensure(bank, 'TZS').gross += tissTipsAmount;

                    if (!isCash) {

                        const bankKey = p.bank_account_details || p.bank_name || 'UNKNOWN';

                        if (!byBank[bankKey]) byBank[bankKey] = {};

                        byBank[bankKey].TZS = (byBank[bankKey].TZS || 0) + tissTipsAmount;

                    }

                }

            }

            const gepgAmount = parseFloat(p.gepg_amount || 0);

            if (p.control_no_type === 'GEPG' && gepgAmount > 0) {

                ensure(all, 'TZS').gross += gepgAmount;

                if (isClientPaid) {

                    ensure(clientPaid, 'TZS').gross += gepgAmount;

                } else {

                    ensure(bank, 'TZS').gross += gepgAmount;

                    if (!isCash) {

                        const bankKey = p.bank_account_details || p.bank_name || 'UNKNOWN';

                        if (!byBank[bankKey]) byBank[bankKey] = {};

                        byBank[bankKey].TZS = (byBank[bankKey].TZS || 0) + gepgAmount;

                    }

                }

            }

        });



        return { all, cash, bank, clientPaid, byBank, count: filteredData.length };

    }, [filteredData, activeTab]);



    const selectedExpenseShipper = React.useMemo(() => {

        if (activeTab !== 4 || selectedExpenses.length === 0) return '';

        const firstSelected = payables.find(p => selectedExpenses.includes(p.id));

        return firstSelected?.shipping_line_name || '';

    }, [payables, selectedExpenses, activeTab]);



    const allExpensesSelected = React.useMemo(() => {

        if (activeTab !== 4) return false;

        const allExpenses = filteredData.every(p =>

            selectedExpenses.includes(p.id) &&

            (!((p.tiss_tips === 'TISS' || p.tiss_tips === 'TIPS') && parseFloat(p.tiss_tips_amount || 0) > 0) || selectedExpenseCharges.includes(getExpenseChargeKey(p, 'tiss-tips'))) &&

            (!(p.control_no_type === 'GEPG' && parseFloat(p.gepg_amount || 0) > 0) || selectedExpenseCharges.includes(getExpenseChargeKey(p, 'gepg')))

        );

        const allInvoices = invoice.length === 0 || invoice.every(inv => selectedInvoice.some(i => i.id === inv.id));

        return (filteredData.length > 0 || invoice.length > 0) && allExpenses && allInvoices;

    }, [activeTab, filteredData, selectedExpenses, selectedExpenseCharges, invoice, selectedInvoice]);





    // Stage 1 — vendor-grouped selection (summary only, no pay action)

    const selectedStage1VendorName = React.useMemo(() => {

        if (activeTab !== 1 || selectedStage1.length === 0) return '';

        const first = payables.find(p => selectedStage1.includes(p.reference_no));

        return first?.shipping_line_name || first?.vendor_name || '';

    }, [payables, selectedStage1, activeTab]);



    const sameVendorStage1Ids = React.useMemo(() => {

        if (activeTab !== 1 || !selectedStage1VendorName) return [];

        return payables

            .filter(p => (p.shipping_line_name || p.vendor_name || '') === selectedStage1VendorName)

            .map(p => p.reference_no);

    }, [payables, selectedStage1VendorName, activeTab]);



    const stage1Totals = React.useMemo(() => {

        if (activeTab !== 1 || selectedStage1.length === 0) return null;

        const selected = payables.filter(p => selectedStage1.includes(p.reference_no));

        const result = {};

        selected.forEach(p => {

            const curr = p.currency || 'TZS';

            if (!result[curr]) result[curr] = { net: 0, vat: 0, gross: 0 };

            const invoiceCurrencyDifferent = p.currency === 'TZS' && parseFloat(p.usd_rate || 0) > 100;

            const hasOldItems = p.items && p.items.some(item => !item.foreign_amount || item.foreign_amount === 0) && p.items.some(item => !item.total_amount);

            const rawTotal = parseFloat(p.total_amount || 0);

            const gross = invoiceCurrencyDifferent && hasOldItems && p.usd_rate

                ? rawTotal * parseFloat(p.usd_rate)

                : rawTotal;

            const vat = parseFloat(p.total_vat || 0) * (invoiceCurrencyDifferent && hasOldItems && p.usd_rate ? parseFloat(p.usd_rate) : 1);

            result[curr].net += gross - vat;

            result[curr].vat += vat;

            result[curr].gross += gross;

        });

        Object.keys(result).forEach(curr => {

            result[curr].net = Math.round(result[curr].net * 100) / 100;

            result[curr].vat = Math.round(result[curr].vat * 100) / 100;

            result[curr].gross = Math.round(result[curr].gross * 100) / 100;

        });

        return result;

    }, [payables, selectedStage1, activeTab]);



    const handleStage1SelectToggle = (payable) => {

        const key = payable.reference_no;

        const isSelected = selectedStage1.includes(key);

        if (isSelected) {

            setSelectedStage1(prev => prev.filter(k => k !== key));

            return;

        }

        const payableVendorName = payable.shipping_line_name || payable.vendor_name || '';

        if (selectedStage1VendorName && payableVendorName !== selectedStage1VendorName) {

            setShowStage1VendorDialog(true);

            return;

        }

        setSelectedStage1(prev => [...prev, key]);

    };



    // Stage 2 — vendor-grouped selection

    const selectedStage2VendorName = React.useMemo(() => {

        if (activeTab !== 2 || selectedStage2.length === 0) return '';

        const first = payables.find(p => selectedStage2.includes(p.reference_no) && p._payable_source === 'shipping');

        return first?.shipping_line_name || '';

    }, [payables, selectedStage2, activeTab]);



    const sameVendorStage2Ids = React.useMemo(() => {

        if (activeTab !== 2 || !selectedStage2VendorName) return [];

        return payables

            .filter(p => p._payable_source === 'shipping' && (p.shipping_line_name || '') === selectedStage2VendorName)

            .map(p => p.reference_no);

    }, [payables, selectedStage2VendorName, activeTab]);



    const stage2Totals = React.useMemo(() => {

        if (activeTab !== 2 || selectedStage2.length === 0) return null;

        const selected = payables.filter(p => selectedStage2.includes(p.reference_no));

        const result = {};

        selected.forEach(p => {

            const curr = p.currency || 'TZS';

            if (!result[curr]) result[curr] = { net: 0, vat: 0, gross: 0 };

            const invoiceCurrencyDifferent = p.currency === 'TZS' && parseFloat(p.usd_rate || 0) > 100;

            const hasOldItems = p.items && p.items.some(item => !item.foreign_amount || item.foreign_amount === 0) && p.items.some(item => !item.total_amount);

            const rawTotal = parseFloat(p.total_amount || 0);

            const gross = invoiceCurrencyDifferent && hasOldItems && p.usd_rate

                ? rawTotal * parseFloat(p.usd_rate)

                : rawTotal;

            const vat = parseFloat(p.total_vat || 0) * (invoiceCurrencyDifferent && hasOldItems && p.usd_rate ? parseFloat(p.usd_rate) : 1);

            result[curr].net += gross - vat;

            result[curr].vat += vat;

            result[curr].gross += gross;

        });

        Object.keys(result).forEach(curr => {

            result[curr].net = Math.round(result[curr].net * 100) / 100;

            result[curr].vat = Math.round(result[curr].vat * 100) / 100;

            result[curr].gross = Math.round(result[curr].gross * 100) / 100;

        });

        return result;

    }, [payables, selectedStage2, activeTab]);



    // Stage 3 — vendor-grouped selection

    const selectedStage3VendorName = React.useMemo(() => {

        if (activeTab !== 3 || selectedStage3.length === 0) return '';

        const first = payables.find(p => selectedStage3.includes(p.reference_no));

        return first?.shipping_line_name || first?.vendor_name || '';

    }, [payables, selectedStage3, activeTab]);



    const sameVendorStage3Ids = React.useMemo(() => {

        if (activeTab !== 3 || !selectedStage3VendorName) return [];

        return payables

            .filter(p => (p.shipping_line_name || p.vendor_name || '') === selectedStage3VendorName)

            .map(p => p.reference_no);

    }, [payables, selectedStage3VendorName, activeTab]);



    const stage3Totals = React.useMemo(() => {

        if (activeTab !== 3 || selectedStage3.length === 0) return null;

        const selected = payables.filter(p => selectedStage3.includes(p.reference_no));

        const result = {};

        selected.forEach(p => {

            const curr = p.currency || 'TZS';

            if (!result[curr]) result[curr] = { net: 0, vat: 0, gross: 0 };

            const invoiceCurrencyDifferent = p.currency === 'TZS' && parseFloat(p.usd_rate || 0) > 100;

            const hasOldItems = p.items && p.items.some(item => !item.foreign_amount || item.foreign_amount === 0) && p.items.some(item => !item.total_amount);

            const rawTotal = parseFloat(p.total_amount || 0);

            const gross = invoiceCurrencyDifferent && hasOldItems && p.usd_rate

                ? rawTotal * parseFloat(p.usd_rate)

                : rawTotal;

            const vat = parseFloat(p.total_vat || 0) * (invoiceCurrencyDifferent && hasOldItems && p.usd_rate ? parseFloat(p.usd_rate) : 1);

            result[curr].net += gross - vat;

            result[curr].vat += vat;

            result[curr].gross += gross;

        });

        Object.keys(result).forEach(curr => {

            result[curr].net = Math.round(result[curr].net * 100) / 100;

            result[curr].vat = Math.round(result[curr].vat * 100) / 100;

            result[curr].gross = Math.round(result[curr].gross * 100) / 100;

        });

        return result;

    }, [payables, selectedStage3, activeTab]);



    const handleStage2SelectToggle = (payable) => {

        const key = payable.reference_no;

        const isSelected = selectedStage2.includes(key);

        if (isSelected) {

            setSelectedStage2(prev => prev.filter(k => k !== key));

            return;

        }

        if (selectedStage2VendorName && (payable.shipping_line_name || '') !== selectedStage2VendorName) {

            setShowStage2VendorDialog(true);

            return;

        }

        setSelectedStage2(prev => [...prev, key]);

    };



    const handleStage3SelectToggle = (payable) => {

        const key = payable.reference_no;

        const isSelected = selectedStage3.includes(key);

        if (isSelected) {

            setSelectedStage3(prev => prev.filter(k => k !== key));

            return;

        }

        const payableVendorName = payable.shipping_line_name || payable.vendor_name || '';

        if (selectedStage3VendorName && payableVendorName !== selectedStage3VendorName) {

            setShowStage3VendorDialog(true);

            return;

        }

        setSelectedStage3(prev => [...prev, key]);

    };



    const handleExpenseSelectToggle = (payable) => {

        const isCurrentlySelected = selectedExpenses.includes(payable.id);



        if (isCurrentlySelected) {

            setSelectedExpenses(prev => prev.filter(id => id !== payable.id));

            return;

        }



        setSelectedExpenses(prev => [...prev, payable.id]);

    };



    const handleExpenseChargeSelectToggle = (payable, chargeType) => {

        const chargeKey = getExpenseChargeKey(payable, chargeType);

        setSelectedExpenseCharges(prev => prev.includes(chargeKey)

            ? prev.filter(key => key !== chargeKey)

            : [...prev, chargeKey]);

    }; 



    const emptyRowColSpan = activeTab === 4 ? 16 : ((activeTab === 5 || activeTab === 6) ? 14 : ([1, 2].includes(activeTab) ? 14 : 13));



    return (

        <div className="page-container">

            <div className="admin-header" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: '16px' }}>

                <div style={{ display: 'flex', alignItems: 'center', gap: '16px' }}>

                    <h2 style={{ margin: 0 }}>{pageTitle}</h2>

                </div>

                <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>

                    <button onClick={() => { fetchPayables(); fetchCounts(); }} title="Refresh" style={{ background: 'none', border: '1px solid transparent', borderRadius: 6, cursor: 'pointer', fontSize: 18, color: '#444', padding: '4px 8px', transition: 'background 0.15s, border-color 0.15s' }} onMouseEnter={e => { e.currentTarget.style.background = '#f0f0f0'; e.currentTarget.style.borderColor = '#bbb'; }} onMouseLeave={e => { e.currentTarget.style.background = 'none'; e.currentTarget.style.borderColor = 'transparent'; }}><i className="fa-solid fa-rotate-right" style={{ fontWeight: 900 }} /></button>

                    {canManageTasacDocument && !officeMode && (

                        <button

                            type="button"

                            className="btn btn-link"

                            style={{ fontWeight: 600, whiteSpace: 'nowrap' }}

                            onClick={() => {

                                setTasacFile(null);

                                setShowTasacModal(true);

                            }}

                        >

                            <i className="fa fa-file-pdf-o" style={{ marginRight: '6px' }}></i>

                            Tasac Fees Document

                        </button>

                    )}

                </div>

            </div>

            

            <div className="custom-tabs-container" style={{ marginTop: 20 }}>

                 {allStages.filter(s => myStages.includes(s.id)).map(s => {

                     const count = stageCounts[s.id] || 0;

                     return (

                        <button 

                            key={s.id} 

                            className={[

                                'custom-tab',

                                activeTab === s.id ? 'active' : '',

                                officeMode && s.id !== 4 ? 'cash-office-tab' : '',

                                s.id === 4 ? 'expenses-tab' : '',

                                s.id === 3 && count > 0 ? 'stage-3-flashing' : ''

                            ].filter(Boolean).join(' ')}

                            onClick={() => setActiveTab(s.id)}

                        >

                            {s.name} ({count})

                        </button>

                     );

                 })}

            </div>



            <div className="tab-content" style={{ padding: 20, background: 'white', border: '1px solid #ddd' }}>

                 <h3 style={{ margin: '0 0 10px 0' }}>{currentStage?.name}</h3>

                 <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '10px' }}>

                    <div style={{ display: 'flex', alignItems: 'center', gap: '20px' }}>

                        {activeTab === 5 && (

                            <select 

                                className="form-control" 

                                style={{ width: '250px' }}

                                value={filterStage}

                                onChange={e => {

                                    setFilterStage(e.target.value);

                                    setFilterText(''); // Clear text filter when stage filter changes

                                }}

                            >

                                <option value="">Filter Sent To (All)</option>

                                <option value="Creator">Sent To: Creator</option>

                                <option value="Stage 1">Sent To: Stage 1</option>

                                <option value="Stage 2">Sent To: Stage 2</option>

                                {/* <option value="Stage 3">Sent To: Stage 3 (If supported)</option> */}

                            </select>

                        )}

                        {activeTab === 4 && (

                            <div style={{ display: 'flex', gap: '10px', alignItems: 'flex-end' }}>

                                <div>

                                    <label style={{ fontSize: 11, color: '#666', display: 'block', marginBottom: 3 }}>VIEW MODE</label>

                                    <div style={{ display: 'flex', borderRadius: 6, overflow: 'hidden', border: '1px solid #ced4da' }}>

                                        {EXPENSE_FILTER_MODES.map(mode => (

                                            <button

                                                key={mode.id}

                                                onClick={() => {

                                                    setExpenseFilter(mode.id);

                                                    setExpenseSearch('');

                                                    setExpenseSearchParam('');

                                                    setFilterText('');

                                                }}

                                                style={{

                                                    padding: '6px 14px', border: 'none', cursor: 'pointer', fontSize: '0.85rem',

                                                    background: expenseFilter === mode.id ? '#1a73e8' : '#fff',

                                                    color: expenseFilter === mode.id ? '#fff' : '#333',

                                                    fontWeight: expenseFilter === mode.id ? 600 : 400,

                                                }}

                                            >

                                                {mode.label}

                                            </button>

                                        ))}

                                    </div>

                                </div>



                                {expenseFilter === 'range' && (

                                    <>

                                        <div style={{display:'flex', alignItems:'center', gap:'5px'}}>

                                            <label style={{marginBottom:0, fontSize:'0.9em'}}>From:</label>

                                            <input 

                                                type="date" 

                                                className="form-control" 

                                                value={expenseStartDate}

                                                onChange={e => {

                                                    setExpenseStartDate(e.target.value);

                                                    expenseStartDateRef.current = e.target.value;

                                                }}

                                                readOnly={expenseFilter !== 'range'}

                                                style={{width:'120px', backgroundColor: expenseFilter !== 'range' ? '#e9ecef' : '#fff'}} 

                                            />

                                        </div>

                                        <div style={{display:'flex', alignItems:'center', gap:'5px'}}>

                                            <label style={{marginBottom:0, fontSize:'0.9em'}}>To:</label>

                                            <input 

                                                type="date" 

                                                className="form-control" 

                                                value={expenseEndDate}

                                                onChange={e => {

                                                    setExpenseEndDate(e.target.value);

                                                    expenseEndDateRef.current = e.target.value;

                                                }}

                                                readOnly={expenseFilter !== 'range'}

                                                style={{width:'120px', backgroundColor: expenseFilter !== 'range' ? '#e9ecef' : '#fff'}}

                                            />

                                        </div>

                                        <button type="button" className="btn btn-sm btn-primary" onClick={handleApplyPreviousExpenses}>

                                            <i className="fa fa-filter"></i> Apply

                                        </button>

                                    </>

                                )}



                                {expenseFilter === 'search' && (

                                    <>

                                    <div style={{ width: '150px' }}>

                                        <Select2Dropdown

                                            options={expenseSearchOptions}

                                            value={expenseSearchParam}

                                            onChange={(val) => { setExpenseSearchParam(val); expenseSearchParamRef.current = val; }}

                                            placeholder="Select Field..."

                                            valueKey="id"

                                            displayKey="text"

                                        />

                                    </div>

                                    <div style={{ position: 'relative', width: '150px' }}>

                                    <input 

                                        type="text"

                                        className="form-control"

                                        style={{ width: '100%' }}

                                        placeholder={`Search ${expenseSearchOptions.find(o => o.id === expenseSearchParam)?.text || 'Term'}...`}

                                        value={expenseSearch}

                                        onChange={e => { setExpenseSearch(e.target.value); expenseSearchRef.current = e.target.value; setVendorActiveIdx(-1); }}

                                        onKeyDown={e => {
                                            if (e.key === 'ArrowDown') { e.preventDefault(); setVendorActiveIdx(i => Math.min(i + 1, vendorSuggests.length - 1)); }
                                            else if (e.key === 'ArrowUp') { e.preventDefault(); setVendorActiveIdx(i => Math.max(i - 1, 0)); }
                                            else if (e.key === 'Enter') {
                                                if (vendorActiveIdx >= 0 && vendorSuggests[vendorActiveIdx]) {
                                                    e.preventDefault();
                                                    selectVendorSuggestion(vendorSuggests[vendorActiveIdx]);
                                                } else {
                                                    setVendorSuggests([]);
                                                    fetchPayables();
                                                }
                                            }
                                            else if (e.key === 'Escape') { setVendorSuggests([]); setVendorActiveIdx(-1); }
                                        }}

                                        disabled={!expenseSearchParam}

                                    />

                                    {vendorSuggests.length > 0 && (
                                        <div style={{ position: 'absolute', top: 'calc(100% + 2px)', left: 0, width: '100%', background: '#fff', border: '1px solid #ccc', borderRadius: 4, boxShadow: '0 4px 12px rgba(0,0,0,0.18)', zIndex: 1000, maxHeight: 240, overflowY: 'auto' }}>
                                            {vendorSuggests.map((opt, idx) => (
                                                <button
                                                    type="button"
                                                    key={opt}
                                                    onClick={() => selectVendorSuggestion(opt)}
                                                    onMouseEnter={() => setVendorActiveIdx(idx)}
                                                    style={{ display: 'block', width: '100%', padding: '6px 8px', textAlign: 'left', background: idx === vendorActiveIdx ? '#0d6efd' : 'transparent', color: idx === vendorActiveIdx ? '#fff' : '#222', border: 'none', borderBottom: '1px solid #f0f0f0', cursor: 'pointer', fontSize: 12 }}
                                                >
                                                    {opt}
                                                </button>
                                            ))}
                                        </div>
                                    )}

                                    </div>

                                    <button className="btn btn-sm btn-primary" onClick={fetchPayables} disabled={!expenseSearchParam}>

                                        <i className="fa fa-search"></i>

                                    </button>

                                    </>

                                )}

                                <input 

                                    type="text"

                                    className="form-control"

                                    style={{ width: '150px' }}

                                    placeholder="Search all columns..."

                                    value={filterText}

                                    onChange={e => setFilterText(e.target.value)}

                                />

                                <select

                                    className="form-control"

                                    style={{ width: '180px' }}

                                    value={expenseTypeFilter}

                                    onChange={e => setExpenseTypeFilter(e.target.value)}

                                >

                                    <option value="all">All Expenses</option>

                                    <option value="file">File Expenses</option>

                                    <option value="office">Cash/Office Expenses</option>

                                    <option value="totalcash">Total Cash Expenses</option>

                                    <option value="cash">Cash Expenses</option>

                                    <option value="operation">Operation Expenses</option>

                                    <option value="bank">Bank Expenses</option>

                                    <option value="bank_details">Bank Details</option>

                                </select>

                                <button type="button" className="btn btn-md" style={{ backgroundColor: '#198754', borderColor: '#198754', color: '#fff', width: '15px', padding: '6px 0', textAlign: 'center', marginTop: '17px' }} onClick={exportExpensesExcel}>

                                    <i className="fa fa-file-excel"></i>

                                </button>

                            </div>

                        )}

                    </div>

                    <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>

                        {/* General filter for non-Expense tabs */}

                        {activeTab !== 4 && (

                            <input 

                                type="text" 

                                placeholder="Filter all columns..." 

                                className="form-control" 

                                style={{ width: '300px' }}

                                value={filterText}

                                onChange={e => setFilterText(e.target.value)}

                            />

                        )}

                    </div>

                 </div>



                 {/* Expense Summary Cards */}

                 {activeTab === 4 && expenseSummary && (

                    <div style={{ display: 'flex', gap: 12, marginBottom: 12, flexWrap: 'wrap', alignItems: 'flex-start', textTransform: 'uppercase' }}>

                        {/* Date + Count */}

                        <div style={{ display: 'flex', flexDirection: 'row', gap: 8 }}>

                            <div style={{ background: '#fff3e0', border: '1px solid #ffcc80', borderRadius: 8, padding: '10px 20px', minWidth: 140 }}>

                                <div style={{ fontSize: 11, color: '#e65100', fontWeight: 600 }}>DATE</div>

                                {expenseFilter === 'search' ? (

                                    <div style={{ fontSize: '0.85rem', fontWeight: 700, color: '#bf360c' }}>Search Results</div>

                                ) : expenseFilter === 'daily' ? (

                                    <div style={{ fontSize: '0.95rem', fontWeight: 700, color: '#bf360c' }}>{new Date().toLocaleDateString('en-GB')}</div>

                                ) : (

                                    <div style={{ fontSize: '0.85rem', fontWeight: 700, color: '#bf360c' }}>

                                        {expenseStartDate ? new Date(expenseStartDate).toLocaleDateString('en-GB') : '—'}

                                        <span style={{ margin: '0 4px' }}>→</span>

                                        {expenseEndDate ? new Date(expenseEndDate).toLocaleDateString('en-GB') : '—'}

                                    </div>

                                )}

                            </div>

                            <div style={{ background: '#e3f2fd', border: '1px solid #90caf9', borderRadius: 8, padding: '10px 20px', minWidth: 100 }}>

                                <div style={{ fontSize: 11, color: '#1565c0', fontWeight: 600 }}>COUNT</div>

                                <div style={{ fontSize: '1.3rem', fontWeight: 700, color: '#0d47a1' }}>{expenseSummary.count}</div>

                            </div>

                        </div>

                        {/* Totals by currency */}

                        {Object.keys(expenseSummary.all).length > 0 && (

                            <div style={{ background: '#e8f5e9', border: '1px solid #a5d6a7', borderRadius: 8, padding: '10px 16px', minWidth: 180 }}>

                                <div style={{ fontSize: 11, color: '#388e3c', fontWeight: 700, marginBottom: 6 }}>TOTALS BY CURRENCY</div>

                                {Object.entries(expenseSummary.all).map(([cur, data]) => (

                                    <div key={cur} style={{ borderBottom: '1px solid #c8e6c9', padding: '3px 0', marginBottom: 4 }}>

                                        <div style={{ fontWeight: 700, color: '#2e7d32', fontSize: '0.85rem' }}>{cur}</div>

                                        <div style={{ display: 'flex', justifyContent: 'space-between', gap: 16, fontSize: '0.8rem' }}>

                                            <span style={{ color: '#388e3c' }}>Amount:</span>

                                            <span style={{ fontWeight: 700, color: '#1b5e20' }}>{data.net.toLocaleString(undefined, { minimumFractionDigits: 2 })}</span>

                                        </div>

                                        <div style={{ display: 'flex', justifyContent: 'space-between', gap: 16, fontSize: '0.8rem' }}>

                                            <span style={{ color: '#e65100' }}>VAT:</span>

                                            <span style={{ fontWeight: 700, color: '#e65100' }}>{data.vat.toLocaleString(undefined, { minimumFractionDigits: 2 })}</span>

                                        </div>

                                        <div style={{ display: 'flex', justifyContent: 'space-between', gap: 16, fontSize: '0.85rem', borderTop: '1px dashed #a5d6a7', marginTop: 2, paddingTop: 2 }}>

                                            <span style={{ fontWeight: 700, color: '#1b5e20' }}>Total:</span>

                                            <span style={{ fontWeight: 700, color: '#1b5e20' }}>{data.gross.toLocaleString(undefined, { minimumFractionDigits: 2 })}</span>

                                        </div>

                                    </div>

                                ))}

                            </div>

                        )}

                        {/* Totals by Cash */}

                        {Object.keys(expenseSummary.cash).length > 0 && (

                            <div style={{ background: '#fff8e1', border: '1px solid #ffe082', borderRadius: 8, padding: '10px 16px', minWidth: 180 }}>

                                <div style={{ fontSize: 11, color: '#f57f17', fontWeight: 700, marginBottom: 6 }}>TOTALS BY CASH</div>

                                {Object.entries(expenseSummary.cash).map(([cur, data]) => (

                                    <div key={cur} style={{ borderBottom: '1px solid #ffecb3', padding: '3px 0', marginBottom: 4 }}>

                                        <div style={{ fontWeight: 700, color: '#e65100', fontSize: '0.85rem' }}>{cur}</div>

                                        <div style={{ display: 'flex', justifyContent: 'space-between', gap: 16, fontSize: '0.8rem' }}>

                                            <span style={{ color: '#e65100' }}>Amount:</span>

                                            <span style={{ fontWeight: 700, color: '#1b5e20' }}>{data.net.toLocaleString(undefined, { minimumFractionDigits: 2 })}</span>

                                        </div>

                                        <div style={{ display: 'flex', justifyContent: 'space-between', gap: 16, fontSize: '0.8rem' }}>

                                            <span style={{ color: '#e65100' }}>VAT:</span>

                                            <span style={{ fontWeight: 700, color: '#e65100' }}>{data.vat.toLocaleString(undefined, { minimumFractionDigits: 2 })}</span>

                                        </div>

                                        <div style={{ display: 'flex', justifyContent: 'space-between', gap: 16, fontSize: '0.85rem', borderTop: '1px dashed #ffe082', marginTop: 2, paddingTop: 2 }}>

                                            <span style={{ fontWeight: 700, color: '#bf360c' }}>Total:</span>

                                            <span style={{ fontWeight: 700, color: '#bf360c' }}>{data.gross.toLocaleString(undefined, { minimumFractionDigits: 2 })}</span>

                                        </div>

                                    </div>

                                ))}

                            </div>

                        )}

                        {/* Totals by Bank */}

                        {Object.keys(expenseSummary.bank).length > 0 && (

                            <div style={{ background: '#e3f2fd', border: '1px solid #90caf9', borderRadius: 8, padding: '10px 16px', minWidth: 180 }}>

                                <div style={{ fontSize: 11, color: '#1565c0', fontWeight: 700, marginBottom: 6 }}>TOTALS BY BANK</div>

                                {Object.entries(expenseSummary.bank).map(([cur, data]) => (

                                    <div key={cur} style={{ borderBottom: '1px solid #bbdefb', padding: '3px 0', marginBottom: 4 }}>

                                        <div style={{ fontWeight: 700, color: '#0d47a1', fontSize: '0.85rem' }}>{cur}</div>

                                        <div style={{ display: 'flex', justifyContent: 'space-between', gap: 16, fontSize: '0.8rem' }}>

                                            <span style={{ color: '#1565c0' }}>Amount:</span>

                                            <span style={{ fontWeight: 700, color: '#1b5e20' }}>{data.net.toLocaleString(undefined, { minimumFractionDigits: 2 })}</span>

                                        </div>

                                        <div style={{ display: 'flex', justifyContent: 'space-between', gap: 16, fontSize: '0.8rem' }}>

                                            <span style={{ color: '#e65100' }}>VAT:</span>

                                            <span style={{ fontWeight: 700, color: '#e65100' }}>{data.vat.toLocaleString(undefined, { minimumFractionDigits: 2 })}</span>

                                        </div>

                                        <div style={{ display: 'flex', justifyContent: 'space-between', gap: 16, fontSize: '0.85rem', borderTop: '1px dashed #90caf9', marginTop: 2, paddingTop: 2 }}>

                                            <span style={{ fontWeight: 700, color: '#0d47a1' }}>Total:</span>

                                            <span style={{ fontWeight: 700, color: '#0d47a1' }}>{data.gross.toLocaleString(undefined, { minimumFractionDigits: 2 })}</span>

                                        </div>

                                    </div>

                                ))}

                            </div>

                        )}

                        {/* Totals by Client Paid */}

                        {Object.keys(expenseSummary.clientPaid).length > 0 && (

                            <div style={{ background: '#e0f7fa', border: '1px solid #4dd0e1', borderRadius: 8, padding: '10px 16px', minWidth: 180 }}>

                                <div style={{ fontSize: 11, color: '#006064', fontWeight: 700, marginBottom: 6 }}>TOTALS BY CLIENT PAID</div>

                                {Object.entries(expenseSummary.clientPaid).map(([cur, data]) => (

                                    <div key={cur} style={{ borderBottom: '1px solid #b2ebf2', padding: '3px 0', marginBottom: 4 }}>

                                        <div style={{ fontWeight: 700, color: '#006064', fontSize: '0.85rem' }}>{cur}</div>

                                        <div style={{ display: 'flex', justifyContent: 'space-between', gap: 16, fontSize: '0.8rem' }}>

                                            <span style={{ color: '#006064' }}>Amount:</span>

                                            <span style={{ fontWeight: 700, color: '#1b5e20' }}>{data.net.toLocaleString(undefined, { minimumFractionDigits: 2 })}</span>

                                        </div>

                                        <div style={{ display: 'flex', justifyContent: 'space-between', gap: 16, fontSize: '0.8rem' }}>

                                            <span style={{ color: '#e65100' }}>VAT:</span>

                                            <span style={{ fontWeight: 700, color: '#e65100' }}>{data.vat.toLocaleString(undefined, { minimumFractionDigits: 2 })}</span>

                                        </div>

                                        <div style={{ display: 'flex', justifyContent: 'space-between', gap: 16, fontSize: '0.85rem', borderTop: '1px dashed #4dd0e1', marginTop: 2, paddingTop: 2 }}>

                                            <span style={{ fontWeight: 700, color: '#006064' }}>Total:</span>

                                            <span style={{ fontWeight: 700, color: '#006064' }}>{data.gross.toLocaleString(undefined, { minimumFractionDigits: 2 })}</span>

                                        </div>

                                    </div>

                                ))}

                            </div>

                        )}

                        {/* Per bank account totals */}

                        {Object.keys(expenseSummary.byBank).length > 0 && (

                            <div style={{ background: '#f3e5f5', border: '1px solid #ce93d8', borderRadius: 8, padding: '10px 16px', minWidth: 170 }}>

                                <div style={{ fontSize: 11, color: '#6a1b9a', fontWeight: 700, marginBottom: 6 }}>TOTALS BY BANK ACCOUNT</div>

                                {Object.entries(expenseSummary.byBank).map(([bankName, amounts]) => (

                                    <div key={bankName} style={{ display: 'flex', justifyContent: 'space-between', gap: 12, fontSize: '0.85rem', borderBottom: '1px solid #e1bee7', padding: '3px 0' }}>

                                        <span style={{ fontWeight: 700, color: '#6a1b9a', textTransform: 'uppercase' }}>{bankName}</span>

                                        <div style={{ fontWeight: 700, color: '#4a148c', textAlign: 'right' }}>

                                            {Object.entries(amounts).map(([currency, amount]) => (

                                                <div key={currency}>{currency} {amount.toLocaleString(undefined, { minimumFractionDigits: 2 })}</div>

                                            ))}

                                        </div>

                                    </div>

                                ))}

                            </div>

                        )}

                    </div>

                 )}

                 

                 {/* Running Totals Row for Expenses */}

                 {activeTab === 4 && expenseTotals && (

                    <div style={{ marginBottom: '15px', padding: '15px', background: '#e3f2fd', border: '1px solid #90caf9', borderRadius: '4px' }}>

                        <div style={{ marginBottom: '8px', fontWeight: 'bold', fontSize: '1.05em', color: '#0d47a1' }}>

                            SHIPPER: {selectedExpenseShipper || '-'}

                        </div>

                        <h5 style={{ margin: '0 0 10px 0', color: '#1565c0' }}>

                            <i className="fa fa-calculator"></i> Selected Items Summary ({selectedExpenses.length + selectedExpenseCharges.length + selectedInvoice.length})

                        </h5>

                        <div style={{ display: 'flex', gap: '20px', flexWrap: 'wrap' }}>

                            {Object.entries(expenseTotals).map(([currency, data]) => (

                                <div key={currency} style={{ padding: '10px', background: 'white', borderRadius: '4px', boxShadow: '0 1px 3px rgba(0,0,0,0.1)', minWidth: '200px' }}>

                                    <div style={{ fontWeight: 'bold', borderBottom: '1px solid #eee', marginBottom: '5px' }}>{currency}</div>

                                    <div style={{ display: 'flex', justifyContent: 'space-between' }}><span>Amount:</span> <strong>{data.net.toLocaleString(undefined, {minimumFractionDigits: 2})}</strong></div>

                                    <div style={{ display: 'flex', justifyContent: 'space-between' }}><span>VAT:</span> <strong>{data.vat.toLocaleString(undefined, {minimumFractionDigits: 2})}</strong></div>

                                    <div style={{ display: 'flex', justifyContent: 'space-between', color: '#1565c0', marginTop: '5px', paddingTop: '5px', borderTop: '1px dashed #ccc' }}>

                                        <span>Total:</span> <strong>{data.gross.toLocaleString(undefined, {minimumFractionDigits: 2})}</strong>

                                    </div>

                                </div>

                            ))}

                        </div>

                        {allExpensesSelected && expenseVendorTotals && Object.keys(expenseVendorTotals).length > 0 && (

                            <div style={{ marginTop: 15 }}>

                                <h6 style={{ color: '#0d47a1', margin: '0 0 8px' }}>PER VENDOR</h6>

                                <div style={{ display: 'flex', gap: '20px', flexWrap: 'wrap' }}>

                                    {Object.entries(expenseVendorTotals).map(([vendor, data]) => (

                                        <div key={vendor} style={{ padding: '10px', background: 'white', borderRadius: '4px', boxShadow: '0 1px 3px rgba(0,0,0,0.1)', minWidth: '200px' }}>

                                            <div style={{ fontWeight: 'bold', borderBottom: '1px solid #eee', marginBottom: '5px', wordBreak: 'break-word' }}>{vendor}</div>

                                            {data.amountTZS > 0 && (

                                                <div style={{ display: 'flex', justifyContent: 'space-between' }}><span>Amount TZS:</span> <strong>{data.amountTZS.toLocaleString(undefined, {minimumFractionDigits: 2})}</strong></div>

                                            )}

                                            {data.amountUSD > 0 && (

                                                <div style={{ display: 'flex', justifyContent: 'space-between' }}><span>Amount USD:</span> <strong>{data.amountUSD.toLocaleString(undefined, {minimumFractionDigits: 2})}</strong></div>

                                            )}

                                            {data.bankChargesTZS > 0 && (

                                                <div style={{ display: 'flex', justifyContent: 'space-between' }}><span>Bank Charges TZS:</span> <strong>{data.bankChargesTZS.toLocaleString(undefined, {minimumFractionDigits: 2})}</strong></div>

                                            )}

                                            {data.bankChargesUSD > 0 && (

                                                <div style={{ display: 'flex', justifyContent: 'space-between' }}><span>Bank Charges USD:</span> <strong>{data.bankChargesUSD.toLocaleString(undefined, {minimumFractionDigits: 2})}</strong></div>

                                            )}

                                        </div>

                                    ))}

                                </div>

                            </div>

                        )}

                    </div>

                 )}



                 {/* Running Totals Row for Stage 1 selection (summary only) */}

                 {activeTab === 1 && stage1Totals && (

                    <div style={{ marginBottom: '15px', padding: '15px', background: '#e8f5e9', border: '1px solid #a5d6a7', borderRadius: '4px' }}>

                        <div style={{ marginBottom: '8px', fontWeight: 'bold', fontSize: '1.05em', color: '#2e7d32' }}>

                            VENDOR: {selectedStage1VendorName || '-'}

                        </div>

                        <h5 style={{ margin: '0 0 10px 0', color: '#388e3c' }}>

                            <i className="fa fa-calculator"></i> Selected Items Summary ({selectedStage1.length})

                        </h5>

                        <div style={{ display: 'flex', gap: '20px', flexWrap: 'wrap' }}>

                            {Object.entries(stage1Totals).map(([currency, data]) => (

                                <div key={currency} style={{ padding: '10px', background: 'white', borderRadius: '4px', boxShadow: '0 1px 3px rgba(0,0,0,0.1)', minWidth: '200px' }}>

                                    <div style={{ fontWeight: 'bold', borderBottom: '1px solid #eee', marginBottom: '5px' }}>{currency}</div>

                                    <div style={{ display: 'flex', justifyContent: 'space-between' }}><span>Amount:</span> <strong>{data.net.toLocaleString(undefined, {minimumFractionDigits: 2})}</strong></div>

                                    <div style={{ display: 'flex', justifyContent: 'space-between' }}><span>VAT:</span> <strong>{data.vat.toLocaleString(undefined, {minimumFractionDigits: 2})}</strong></div>

                                    <div style={{ display: 'flex', justifyContent: 'space-between', color: '#388e3c', marginTop: '5px', paddingTop: '5px', borderTop: '1px dashed #ccc' }}>

                                        <span>Total:</span> <strong>{data.gross.toLocaleString(undefined, {minimumFractionDigits: 2})}</strong>

                                    </div>

                                </div>

                            ))}

                        </div>

                    </div>

                 )}



                 {/* Running Totals Row for Stage 2 bulk-pay selection */}

                 {activeTab === 2 && stage2Totals && (

                    <div style={{ marginBottom: '15px', padding: '15px', background: '#e8f5e9', border: '1px solid #a5d6a7', borderRadius: '4px' }}>

                        <div style={{ marginBottom: '8px', fontWeight: 'bold', fontSize: '1.05em', color: '#2e7d32' }}>

                            VENDOR: {selectedStage2VendorName || '-'}

                        </div>

                        <h5 style={{ margin: '0 0 10px 0', color: '#388e3c' }}>

                            <i className="fa fa-calculator"></i> Selected Items Summary ({selectedStage2.length})

                        </h5>

                        <div style={{ display: 'flex', gap: '20px', flexWrap: 'wrap' }}>

                            {Object.entries(stage2Totals).map(([currency, data]) => (

                                <div key={currency} style={{ padding: '10px', background: 'white', borderRadius: '4px', boxShadow: '0 1px 3px rgba(0,0,0,0.1)', minWidth: '200px' }}>

                                    <div style={{ fontWeight: 'bold', borderBottom: '1px solid #eee', marginBottom: '5px' }}>{currency}</div>

                                    <div style={{ display: 'flex', justifyContent: 'space-between' }}><span>Amount:</span> <strong>{data.net.toLocaleString(undefined, {minimumFractionDigits: 2})}</strong></div>

                                    <div style={{ display: 'flex', justifyContent: 'space-between' }}><span>VAT:</span> <strong>{data.vat.toLocaleString(undefined, {minimumFractionDigits: 2})}</strong></div>

                                    <div style={{ display: 'flex', justifyContent: 'space-between', color: '#388e3c', marginTop: '5px', paddingTop: '5px', borderTop: '1px dashed #ccc' }}>

                                        <span>Total:</span> <strong>{data.gross.toLocaleString(undefined, {minimumFractionDigits: 2})}</strong>

                                    </div>

                                </div>

                            ))}

                        </div>

                    </div>

                 )}



                 {/* Running Totals Row for Stage 3 bulk-approval selection */}

                 {activeTab === 3 && stage3Totals && (

                    <div style={{ marginBottom: '15px', padding: '15px', background: '#e8f5e9', border: '1px solid #a5d6a7', borderRadius: '4px' }}>

                        <div style={{ marginBottom: '8px', fontWeight: 'bold', fontSize: '1.05em', color: '#2e7d32' }}>

                            VENDOR: {selectedStage3VendorName || '-'}

                        </div>

                        <h5 style={{ margin: '0 0 10px 0', color: '#388e3c' }}>

                            <i className="fa fa-calculator"></i> Selected Items Summary ({selectedStage3.length})

                        </h5>

                        <div style={{ display: 'flex', gap: '20px', flexWrap: 'wrap' }}>

                            {Object.entries(stage3Totals).map(([currency, data]) => (

                                <div key={currency} style={{ padding: '10px', background: 'white', borderRadius: '4px', boxShadow: '0 1px 3px rgba(0,0,0,0.1)', minWidth: '200px' }}>

                                    <div style={{ fontWeight: 'bold', borderBottom: '1px solid #eee', marginBottom: '5px' }}>{currency}</div>

                                    <div style={{ display: 'flex', justifyContent: 'space-between' }}><span>Amount:</span> <strong>{data.net.toLocaleString(undefined, {minimumFractionDigits: 2})}</strong></div>

                                    <div style={{ display: 'flex', justifyContent: 'space-between' }}><span>VAT:</span> <strong>{data.vat.toLocaleString(undefined, {minimumFractionDigits: 2})}</strong></div>

                                    <div style={{ display: 'flex', justifyContent: 'space-between', color: '#388e3c', marginTop: '5px', paddingTop: '5px', borderTop: '1px dashed #ccc' }}>

                                        <span>Total:</span> <strong>{data.gross.toLocaleString(undefined, {minimumFractionDigits: 2})}</strong>

                                    </div>

                                </div>

                            ))}

                        </div>

                    </div>

                 )}



                 <div style={{ width: '100%' }}>

                 <style>{flashStyles}</style>

                 <table className="payables-table" style={{ minWidth: 'max-content' }}>

                     {activeTab === 4 ? (

                         <thead>

                             <tr>

                                 <th onClick={() => requestSort('reference_no')} style={{ cursor: 'pointer' }}>Ref No {getSortIndicator('reference_no')}</th>

                                 <th onClick={() => requestSort('idf_number')} style={{ cursor: 'pointer' }}>IDF No {getSortIndicator('idf_number')}</th>

                                 <th onClick={() => requestSort('job_number')} style={{ cursor: 'pointer' }}>Job ID {getSortIndicator('job_number')}</th>

                                 <th onClick={() => requestSort('bl_number')} style={{ cursor: 'pointer' }}>BL No {getSortIndicator('bl_number')}</th>

                                 <th onClick={() => requestSort('customer_name')} style={{ cursor: 'pointer' }}>Customer Name {getSortIndicator('customer_name')}</th>

                                 <th onClick={() => requestSort('shipping_line_name')} style={{ cursor: 'pointer' }}>{officeMode ? 'Vendor/Staff' : 'Vendor'} {getSortIndicator('shipping_line_name')}</th>

                                 <th onClick={() => requestSort('charge_names')} style={{ cursor: 'pointer' }}>Charge Names {getSortIndicator('charge_names')}</th>

                                 <th onClick={() => requestSort('currency')} style={{ cursor: 'pointer' }}>Currency {getSortIndicator('currency')}</th>

                                 <th onClick={() => requestSort('total_amount')} style={{ cursor: 'pointer' }}>Amount {getSortIndicator('total_amount')}</th>

                                 <th style={{ textAlign: 'center' }}>

                                    <input

                                        type="checkbox"

                                        onChange={(e) => {

                                            const isChecked = e.target.checked;

                                            if (isChecked) {

                                                setSelectedInvoice(invoice);

                                                setSelectedExpenses(filteredData.map(p => p.id));

                                                setSelectedExpenseCharges(filteredData.flatMap(p => [

                                                    ...((p.tiss_tips === 'TISS' || p.tiss_tips === 'TIPS') && parseFloat(p.tiss_tips_amount || 0) > 0 ? [getExpenseChargeKey(p, 'tiss-tips')] : []),

                                                    ...(p.control_no_type === 'GEPG' && parseFloat(p.gepg_amount || 0) > 0 ? [getExpenseChargeKey(p, 'gepg')] : [])

                                                ]));

                                            } else {

                                                setSelectedInvoice([]);

                                                setSelectedExpenses([]);

                                                setSelectedExpenseCharges([]);

                                            }

                                        }}

                                        checked={(() => {

                                            const allExpensesSelected = filteredData.every(p =>

                                                selectedExpenses.includes(p.id) &&

                                                (!((p.tiss_tips === 'TISS' || p.tiss_tips === 'TIPS') && parseFloat(p.tiss_tips_amount || 0) > 0) || selectedExpenseCharges.includes(getExpenseChargeKey(p, 'tiss-tips'))) &&

                                                (!(p.control_no_type === 'GEPG' && parseFloat(p.gepg_amount || 0) > 0) || selectedExpenseCharges.includes(getExpenseChargeKey(p, 'gepg')))

                                            );

                                            const allInvoicesSelected = invoice.length > 0 && invoice.every(inv => selectedInvoice.some(i => i.id === inv.id));

                                            return (filteredData.length > 0 || invoice.length > 0) && allExpensesSelected && (invoice.length === 0 || allInvoicesSelected);

                                        })()}

                                    />

                                 </th>

                                 <th onClick={() => requestSort('payment_date')} style={{ cursor: 'pointer' }}>Payment Date {getSortIndicator('payment_date')}</th>

                                 <th onClick={() => requestSort('payment_method')} style={{ cursor: 'pointer' }}>Payment Method {getSortIndicator('payment_method')}</th>

                                 <th onClick={() => requestSort('payment_ref')} style={{ cursor: 'pointer' }}>Payment Ref {getSortIndicator('payment_ref')}</th>

                                 <th onClick={() => requestSort('paid_by_label')} style={{ cursor: 'pointer' }}>Paid By {getSortIndicator('paid_by_label')}</th>

                                 <th onClick={() => requestSort('approved_by')} style={{ cursor: 'pointer' }}>Approved By {getSortIndicator('approved_by')}</th>

                                 <th>Action</th>

                             </tr>

                         </thead>

                     ) : (

                         <thead>

                             <tr>

                                {activeTab === 1 && (

                                    <th style={{ width: '40px', textAlign: 'center' }}>

                                    </th>

                                )}

                                {activeTab === 2 && (

                                    <th style={{ width: '40px', textAlign: 'center' }}>

                                    </th>

                                )}

                                {activeTab === 3 && (

                                    <th style={{ width: '40px', textAlign: 'center' }}>

                                    </th>

                                )}

                                <th onClick={() => requestSort('created_at')} style={{ cursor: 'pointer', minWidth: [5, 6, 7].includes(activeTab) ? '100px' : undefined }}>Date/Time {getSortIndicator('created_at')}</th>

                                 <th onClick={() => requestSort('reference_no')} style={{ cursor: 'pointer' }}>Ref No {getSortIndicator('reference_no')}</th>

                                 <th onClick={() => requestSort('job_number')} style={{ cursor: 'pointer' }}>Job ID {getSortIndicator('job_number')}</th>

                                 <th onClick={() => requestSort('bl_number')} style={{ cursor: 'pointer' }}>BL No {getSortIndicator('bl_number')}</th>

                                 <th onClick={() => requestSort('idf_number')} style={{ cursor: 'pointer' }}>IDF {getSortIndicator('idf_number')}</th>

                                 <th onClick={() => requestSort('customer_name')} style={{ cursor: 'pointer' }}>Customer {getSortIndicator('customer_name')}</th>

                                 <th onClick={() => requestSort('shipping_line_name')} style={{ cursor: 'pointer' }}>{officeMode ? 'Vendor/Staff' : 'Vendor'} {getSortIndicator('shipping_line_name')}</th>

                                 <th onClick={() => requestSort('total_amount')} style={{ cursor: 'pointer' }}>Amount {getSortIndicator('total_amount')}</th>

                                 <th onClick={() => requestSort('currency')} style={{ cursor: 'pointer' }}>Currency {getSortIndicator('currency')}</th>

                                 <th onClick={() => requestSort('created_by_name')} style={{ cursor: 'pointer' }}>Created By {getSortIndicator('created_by_name')}</th>

                                 {activeTab === 5 && <th onClick={() => requestSort('sent_to_stage')} style={{ cursor: 'pointer' }}>Sent To Stage {getSortIndicator('sent_to_stage')}</th>}

                                 {(activeTab === 5 || activeTab === 6) && <th onClick={() => requestSort('rejection_reason_display')} style={{ cursor: 'pointer' }}>Rejection Reason {getSortIndicator('rejection_reason_display')}</th>}

                                 <th onClick={() => requestSort('history_count')} style={{ cursor: 'pointer' }}>History {getSortIndicator('history_count')}</th>

                                 <th>Action</th>

                             </tr>

                         </thead>

                     )}

                     <tbody>

                         {loading ? (

                             <tr><td colSpan={emptyRowColSpan} className="text-center">Loading...</td></tr>

                         ) : sortedData.length === 0 ? (

                             <tr><td colSpan={emptyRowColSpan} className="text-center">No records found</td></tr>

                         ) : (

                             sortedData.map((p, idx) => (

                                 activeTab === 4 ? (

                                     <React.Fragment key={`${p._payable_source}-${p.id}`}>

                                     <tr style={{ textTransform: 'uppercase' }}>

                                         <td style={getRefCellStyle(p, isDuplicateTransporterJob(p, idx))} title={isDuplicateTransporterJob(p, idx) ? "ADDITIONAL INVOICE OF SAME IDF" : undefined}>{p.reference_no}</td>

                                         <td>{p.idf_number}</td>

                                         <td>{p.job_number}</td>

                                         <td>{p.bl_number}</td>

                                         <td style={{ fontWeight: 'bold' }}>{p.customer_short_name || p.customer_name}</td>

                                         <td>{getVendorDisplay(p)}</td>

                                        <td>{(() => {

                                            const names = p.charge_names ? p.charge_names.split(', ').map(s => s.trim()).filter(Boolean) : [];

                                            if (names.length <= 1) return p.charge_names || '-';

                                            return (

                                                <span title={names.join('\n')} style={{ cursor: 'default' }}>

                                                    {names[0]}<span style={{ color: '#888', fontStyle: 'italic' }}>, ...</span>

                                                </span>

                                            );

                                        })()}</td>

                                         <td>{p.currency}</td>

                                        <td style={{ textAlign: 'right' }}>

                                    {(() => {

                                        const invoiceCurrencyDifferent = p.currency === 'TZS' && parseFloat(p.usd_rate || 0) > 100;

                                        const hasOldItems = p.items && p.items.some(item => !item.foreign_amount || item.foreign_amount === 0) && p.items.some(item => !item.total_amount);

                                        const amount = parseFloat(p.total_amount || 0);

                                        const displayAmount = invoiceCurrencyDifferent && hasOldItems && p.usd_rate

                                            ? Math.round(amount * parseFloat(p.usd_rate) * 100) / 100 

                                            : amount;

                                        return formatAmountDisplay(displayAmount, p);

                                    })()}

                                </td>

                                        <td className="text-center" style={{ textAlign: 'center' }}>

                                            <input 

                                               type="checkbox"

                                               checked={selectedExpenses.includes(p.id)}

                                               onChange={() => handleExpenseSelectToggle(p)}

                                            />

                                        </td>

                                         <td>{formatDateDisplay(p.payment_date)}</td>

                                         <td>{getPaymentMethodDisplay(p)}</td>

                                         <td>{p.cheque_no || '-'}</td>

                                        <td>{getExpensePaidByLabel(p)}</td>

                                         <td>{(p.status === 'CLIENT PAID' || p.status === 'CLIENT_PAID') ? (p.action_by_name || '-') : (p.approved_stage_3_by_name || '-')}</td>

                                        <td>

                                            <button className="icon-btn" style={{color: 'blue'}} onClick={() => handleView(p)} title="View Details">

                                                 <i className="fa fa-eye"></i>

                                             </button>

                                             {hasPermission('PAYABLES', 'can_delete') && (

                                                 <button

                                                     className="icon-btn"

                                                     style={{ color: 'darkorange' }}

                                                     onClick={() => setReverseConfirmDialog({ show: true, payable: p })}

                                                     title={getExpensePaidByLabel(p) === 'CLIENT PAID' ? 'Send to Stage 1' : 'Send to Stage 3'}

                                                 >

                                                     <i className="fa fa-undo"></i>

                                                 </button>

                                             )}

                                         </td>

                                     </tr>

                                     {(p.tiss_tips === 'TISS' || p.tiss_tips === 'TIPS') && parseFloat(p.tiss_tips_amount || 0) > 0 && (

                                         <tr key={`${p.id}-tiss-tips`} style={{ backgroundColor: '#f0f4ff', textTransform: 'uppercase' }}>

                                             <td style={{ ...getRefCellStyle(p, false)}}>{p.reference_no}</td>

                                             <td>{p.idf_number}</td>

                                             <td>{p.job_number}</td>

                                             <td>{p.bl_number}</td>

                                             <td>{p.customer_short_name || p.customer_name}</td>

                                             <td style={{ color: '#555' }}></td>

                                             <td>

                                                 <span

                                                     title={p.bank_name || ''}

                                                     style={{ fontWeight: '600', color: '#1a56db', cursor: 'default' }}

                                                 >

                                                     BANK CHARGES ({p.tiss_tips})

                                                 </span>

                                             </td>

                                             <td>TZS</td>

                                             <td style={{ fontWeight: '600', textAlign: 'right' }}>

                                                 {parseFloat(p.tiss_tips_amount).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}

                                             </td>

                                             <td className="text-center" style={{ textAlign: 'center' }}>

                                                 <input

                                                     type="checkbox"

                                                     checked={selectedExpenseCharges.includes(getExpenseChargeKey(p, 'tiss-tips'))}

                                                     onChange={() => handleExpenseChargeSelectToggle(p, 'tiss-tips')}

                                                 />

                                             </td>

                                             <td>{formatDateDisplay(p.payment_date)}</td>

                                             <td style={{ color: '#555' }}>{getPaymentMethodDisplay(p)}</td>

                                             <td></td>

                                             <td></td>

                                             <td></td>

                                             <td></td>

                                         </tr>

                                     )}

                                     {p.control_no_type === 'GEPG' && parseFloat(p.gepg_amount || 0) > 0 && (

                                         <tr key={`${p.id}-gepg`} style={{ backgroundColor: '#f0f4ff', textTransform: 'uppercase' }}>

                                             <td style={{ ...getRefCellStyle(p, false)}}>{p.reference_no}</td>

                                             <td>{p.idf_number}</td>

                                             <td>{p.job_number}</td>

                                             <td>{p.bl_number}</td>

                                             <td>{p.customer_short_name || p.customer_name}</td>

                                             <td style={{ color: '#555' }}></td>

                                             <td>

                                                 <span

                                                     title={p.bank_name || ''}

                                                     style={{ fontWeight: '600', color: '#1a56db', cursor: 'default' }}

                                                 >

                                                     BANK CHARGES (GEPG)

                                                 </span>

                                             </td>

                                             <td>TZS</td>

                                             <td style={{ fontWeight: '600', textAlign: 'right' }}>

                                                 {formatAmountDisplay(parseFloat(p.gepg_amount || 0), p)}

                                             </td>

                                             <td className="text-center" style={{ textAlign: 'center' }}>

                                                 <input

                                                     type="checkbox"

                                                     checked={selectedExpenseCharges.includes(getExpenseChargeKey(p, 'gepg'))}

                                                     onChange={() => handleExpenseChargeSelectToggle(p, 'gepg')}

                                                 />

                                             </td>

                                             <td>{formatDateDisplay(p.payment_date)}</td>

                                             <td style={{ color: '#555' }}>{getPaymentMethodDisplay(p)}</td>

                                             <td></td>

                                             <td></td>

                                             <td></td>

                                             <td></td>

                                         </tr>

                                     )}

                                     </React.Fragment>

                                 ) : (

                                     <tr key={`${p._payable_source}-${p.id}`}>

                                        {activeTab === 1 && (

                                            <td className="text-center" style={{ textAlign: 'center', verticalAlign: 'middle' }}>

                                                <input

                                                    type="checkbox"

                                                    checked={selectedStage1.includes(p.reference_no)}

                                                    onChange={() => handleStage1SelectToggle(p)}

                                                />

                                            </td>

                                        )}

                                        {activeTab === 2 && (

                                            <td className="text-center" style={{ textAlign: 'center', verticalAlign: 'middle' }}>

                                                

                                                    <input

                                                        type="checkbox"

                                                        checked={selectedStage2.includes(p.reference_no)}

                                                        onChange={() => handleStage2SelectToggle(p)}

                                                    />

                                                

                                            </td>

                                        )}

                                        {activeTab === 3 && (

                                            <td className="text-center" style={{ textAlign: 'center', verticalAlign: 'middle' }}>

                                                <input

                                                    type="checkbox"

                                                    checked={selectedStage3.includes(p.reference_no)}

                                                    onChange={() => handleStage3SelectToggle(p)}

                                                />

                                            </td>

                                        )}

                                        <td>

                                            <div style={{ fontWeight: '600' }}>{new Date(p.created_at).toLocaleDateString('en-GB')}</div>

                                            <div style={{ fontSize: '0.85em', color: '#666', marginTop: '2px' }}>{new Date(p.created_at).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit', hour12: true }).toUpperCase()}</div>

                                         </td>

                                         <td style={getRefCellStyle(p, isDuplicateTransporterJob(p, idx))} title={isDuplicateTransporterJob(p, idx) ? "ADDITIONAL INVOICE OF SAME IDF" : undefined}>{p.reference_no}</td>

                                         <td style={{ fontWeight: 'bold', color: '#007bff' }}>{p.job_number}</td> 

                                         <td>{p.bl_number}</td>

                                        <td>{p.idf_number}</td>

                                        <td style={{ fontWeight: 'bold' }}>{p.customer_short_name || p.customer_name}</td>

                                        <td>{getVendorDisplay(p)}</td>

                                        <td style={{ textAlign: 'right' }}>

                                    {(() => {

                                        const invoiceCurrencyDifferent = p.currency === 'TZS' && parseFloat(p.usd_rate || 0) > 100;

                                        const hasOldItems = p.items && p.items.some(item => !item.foreign_amount || item.foreign_amount === 0) && p.items.some(item => !item.total_amount);

                                        const amount = parseFloat(p.total_amount || 0);

                                        const displayAmount = invoiceCurrencyDifferent && hasOldItems && p.usd_rate

                                            ? amount * parseFloat(p.usd_rate)

                                            : amount;

                                        return formatAmountDisplay(displayAmount, p);

                                    })()}

                                </td>

                                        <td>{p.currency}</td>

                                        <td>{getCreatedByDisplay(p)}</td>

                                         {activeTab === 5 && (

                                             <td>

                                                 {getSentToStageLabel(p)}

                                             </td>

                                         )}

                                         {(activeTab === 5 || activeTab === 6) && (

                                             <td style={{color: 'red'}}>

                                                 {activeTab === 6 ? (p.receipt_rejection_reason || '-') : (p.rejection_reason || '-')}

                                             </td>

                                         )}

                                         <td>

                                             {p.rejections && p.rejections.length > 0 ? (

                                                 <button className="icon-btn" onClick={() => setViewingRejections(p)} title="View Rejection History" style={{ color: 'red' }}>

                                                      <i className="fa fa-exclamation-triangle"></i> ({p.rejections.length})

                                                 </button>

                                             ) : '-'}

                                         </td>

                                         <td>

                                             <div style={{ display: 'flex', gap: '5px', alignItems: 'center' }}>

                                                 <button className="icon-btn" style={{color: 'blue'}} onClick={() => handleView(p)} title="View Details">

                                                     <i className="fa fa-eye"></i>

                                                 </button>

                                                 {(activeTab === 5) && hasPermission('PAYABLES', 'can_delete') && (

                                                     <button className="icon-btn" style={{color: 'red'}} onClick={() => handleDelete(p)} title="Delete">

                                                         <i className="fa fa-trash"></i>

                                                     </button>

                                                 )}

                                             </div>

                                         </td>

                                     </tr>

                                 )

                             ))

                         )}

                     {activeTab === 4 && invoice.length > 0 && (

                        invoice.map(inv => (

                        <tr key={inv.id || inv.invoice_no} style={{ backgroundColor: '#e8f5e9', textTransform: 'uppercase' }}>

                            <td style={{ fontWeight: '600', color: '#2e7d32', whiteSpace: 'nowrap' }}>{inv.invoice_no || '-'}</td>

                            <td>{inv.idf_no || '-'}</td>

                            <td>{inv.job_id || '-'}</td>

                            <td>{inv.bl_number || inv.bl || '-'}</td>

                            <td style={{ fontWeight: 'bold' }}>{inv.customer_short_name || inv.customer_name || '-'}</td>

                            <td>-</td>

                            <td>{(() => {

                                const names = inv.charge_names ? inv.charge_names.split(', ').map(s => s.trim()).filter(Boolean) : [];

                                if (names.length <= 1) return inv.charge_names || '-';

                                return (

                                    <span title={names.join('\n')} style={{ cursor: 'default' }}>

                                        {names[0]}<span style={{ color: '#888', fontStyle: 'italic' }}>, ...</span>

                                    </span>

                                );

                            })()}</td>

                            <td>{inv.currency}</td>

                            <td style={{ textAlign: 'right' }}>{formatAmountDisplay(parseFloat(String(inv.total_amount || '0').replace(/,/g, '')), { currency: inv.currency })}</td>

                            <td className="text-center" style={{ textAlign: 'center' }}>

                                <input

                                    type="checkbox"

                                    checked={selectedInvoice.some(i => i.id === inv.id)}

                                    onChange={() => setSelectedInvoice(prev => prev.some(i => i.id === inv.id) ? prev.filter(i => i.id !== inv.id) : [...prev, inv])}

                                />

                            </td>

                            <td>{formatDateDisplay(inv.invoice_date)}</td>

                            <td>-</td>

                            <td>-</td>

                            <td>-</td>

                            <td>{inv.generated_by_name || '-'}</td>

                            <td>

                                <button className="icon-btn" style={{ color: 'blue' }} onClick={() => handleInvoicePreview(inv)} title="View Invoice">

                                    <i className="fa fa-eye"></i>

                                </button>

                            </td>

                        </tr>

                        ))

                    )}

                    </tbody>

                 </table>

                 </div>

            </div>



             {/* Modal with Form & Actions */}



                 {/* Stage 2 Bulk PAY button */}

                 {activeTab === 2 && selectedStage2.length > 0 && (

                     <div style={{ display: 'flex', justifyContent: 'center', padding: '20px 0 10px', borderTop: '2px solid #dee2e6', marginTop: '4px' }}>

                         <button

                             className="btn btn-success btn-lg"

                             onClick={() => setShowBulkPayForm(true)}

                             style={{ minWidth: '260px', fontWeight: 'bold', letterSpacing: '0.04em' }}

                         >

                             <i className="fa fa-credit-card" style={{ marginRight: '8px' }}></i>

                             PAY ({selectedStage2.length} payable{selectedStage2.length > 1 ? 's' : ''} &mdash; {selectedStage2VendorName})

                         </button>

                     </div>

                 )}



                 {/* Stage 3 Bulk APPROVE button */}

                 {activeTab === 3 && selectedStage3.length > 0 && (

                     <div style={{ display: 'flex', justifyContent: 'center', padding: '20px 0 10px', borderTop: '2px solid #dee2e6', marginTop: '4px' }}>

                         <button

                             className="btn btn-success btn-lg"

                             onClick={() => setShowBulkApprovalForm(true)}

                             style={{ minWidth: '260px', fontWeight: 'bold', letterSpacing: '0.04em' }}

                         >

                             <i className="fa fa-check-circle" style={{ marginRight: '8px' }}></i>

                             APPROVE ({selectedStage3.length} payable{selectedStage3.length > 1 ? 's' : ''} &mdash; {selectedStage3VendorName})

                         </button>

                     </div>

                 )}

             {openPayables.map(({ payable, windowId }) => {

                const canApprovePayable =

                    (permissions?.is_superuser)

                    || (officeMode && activeTab === 7 && myPayablePerms.some(p =>

                        p.stage === 3 && p.can_approve_cash

                    ))

                    || (!officeMode && activeTab === 7)

                    || ([1, 2, 3].includes(activeTab) && myPayablePerms.some(p =>

                        p.stage === activeTab && (officeMode ? p.can_approve_cash : p.can_approve_payables)

                    ));

                return (

                 <PopoutWindow

                     key={windowId}

                     title={`Payable - ${payable.reference_no || payable.invoice_no || payable.id}`}

                     name={`payable_${payable.id}_${windowId}`}

                     toastContainerId={PAYABLE_POPOUT_TOAST_ID}

                            onSave={handleFormSuccess}

                     onClose={() => closePayableWindow(windowId)}

                 >

                     {payable._payable_source !== 'shipping' && officeMode ? (

                         <OfficePayablesForm

                             mode="view"

                             initialData={payable}

                             onClose={() => closePayableWindow(windowId)}

                             module={payable.origin_module || payable.module || 'OFFICE'}

                             adminForTransporter={!!payable?.transporter}

                             approvalMode={canApprovePayable && ![4, 5, 6].includes(activeTab)}

                             canApprove={canApprovePayable}

                             hideClientPaid={activeTab === 7}

                             onApprove={() => {

                                return openConfirmDialog(activeTab === 7 ? 'receipt_approve' : 'approve', payable, windowId);

                            }}

                             onReject={(reason, status, replaceConfig) => {

                                 if (activeTab === 7) return handleReceiptRejectForPayable(payable, reason, replaceConfig, windowId);

                                 return handleRejectForPayable(payable, reason, status || 'REJECTED', windowId);

                             }}

                             rejectReason={rejectReason}

                             setRejectReason={setRejectReason}

                             showRejectInput={showRejectInput}

                             setShowRejectInput={setShowRejectInput}

                             actionLoading={actionLoading}

                             popoutMode={true}

                             toastContainerId={PAYABLE_POPOUT_TOAST_ID}

                            onSave={handleFormSuccess}

                         />

                     ) : payable._payable_source !== 'shipping' ? (

                         <OtherPayablesForm

                             mode="view"

                             receiptUploadOnly={false}

                             initialData={payable}

                             onClose={() => closePayableWindow(windowId)}

                             approvalMode={canApprovePayable && ![5, 6].includes(activeTab)}

                             canApprove={canApprovePayable}

                             hideClientPaid={activeTab === 7}

                             onApprove={() => {

                                return openConfirmDialog(activeTab === 7 ? 'receipt_approve' : 'approve', payable, windowId);

                            }}

                             onReject={(reason, status, replaceConfig) => {

                                 if (activeTab === 7) return handleReceiptRejectForPayable(payable, reason, replaceConfig, windowId);

                                 return handleRejectForPayable(payable, reason, status || 'REJECTED', windowId);

                             }}

                             onSave={handleFormSuccess}

                             onClientPaid={(date) => handleClientPaidForPayable(payable, windowId, date)}

                             rejectReason={rejectReason}

                             setRejectReason={setRejectReason}

                             showRejectInput={showRejectInput}

                             setShowRejectInput={setShowRejectInput}

                             actionLoading={actionLoading}

                             module={payable.module || 'PERMIT'}

                             showViewExpenses={true}

                             popoutMode={true}

                             toastContainerId={PAYABLE_POPOUT_TOAST_ID}

                            

                         />

                     ) : (

                         <ShippingLinePayablesForm

                             mode="view"

                             receiptUploadOnly={false}

                             initialData={payable}

                             onClose={() => closePayableWindow(windowId)}

                             approvalMode={canApprovePayable && ![5, 6].includes(activeTab)}

                             canApprove={canApprovePayable}

                             hideClientPaid={activeTab === 7}

                             onApprove={() => {

                                return openConfirmDialog(activeTab === 7 ? 'receipt_approve' : 'approve', payable, windowId);

                            }}

                             onReject={(reason, status, replaceConfig) => {

                                 if (activeTab === 7) return handleReceiptRejectForPayable(payable, reason, replaceConfig, windowId);

                                 return handleRejectForPayable(payable, reason, status || 'REJECTED', windowId);

                             }}

                             onSave={handleFormSuccess}

                             onClientPaid={(date) => handleClientPaidForPayable(payable, windowId, date)}

                             rejectReason={rejectReason}

                             setRejectReason={setRejectReason}

                             showRejectInput={showRejectInput}

                             setShowRejectInput={setShowRejectInput}

                             actionLoading={actionLoading}

                             tasacLink={getTasacDocumentLink(tasacDocInfo)}

                             showViewExpenses={true}

                             popoutMode={true}

                             toastContainerId={PAYABLE_POPOUT_TOAST_ID}

                            

                         />

                     )}



                      {confirmDialog.show && confirmDialog.windowId === windowId && (

                         <div className="modal-overlay">

                             <div className="dialog-container" style={{ width: '520px', maxWidth: '95%' }}>

                                 <div className="dialog-header" style={{ background: '#0d6efd', color: '#fff', borderRadius: '4px 4px 0 0' }}>

                                     <h3 style={{ margin: 0, color: '#fff' }}>

                                        {confirmDialog.type === 'client_paid'

                                            ? 'Client Paid Approval'

                                            : confirmDialog.type === 'receipt_approve'

                                                ? 'Approve Receipt'

                                                : 'Approve Payable'}

                                    </h3>

                                 </div>

                                 <div className="dialog-body" style={{ padding: '20px' }}>

                                     <p style={{ margin: 0, fontSize: '1rem' }}>

                                        {confirmDialog.type === 'client_paid'

                                            ? 'Do you want to approve Client Paid?'

                                            : confirmDialog.type === 'receipt_approve'

                                                ? 'Are you sure you want to Approve this receipt?'

                                                : 'Are you sure you want to Approve this Payable?'}

                                    </p>

                                 </div>

                                 <div className="dialog-footer">

                                     <button className="dialog-btn dialog-btn-primary" onClick={handleConfirmDialogProceed} disabled={actionLoading}>

                                         Yes

                                     </button>

                                     <button className="dialog-btn dialog-btn-secondary" onClick={closeConfirmDialog} disabled={actionLoading}>

                                         No

                                     </button>

                                 </div>

                             </div>

                         </div>

                      )}

                 </PopoutWindow>

                );

             })}



             {/* Reverse to Stage 3 Confirmation Modal */}

             {reverseConfirmDialog.show && (

                 <div className="modal-overlay">

                     <div className="dialog-container" style={{ width: '520px', maxWidth: '95%' }}>

                         <div className="dialog-header" style={{ background: '#e65100', color: '#fff', borderRadius: '4px 4px 0 0' }}>

                             <h3 style={{ margin: 0, color: '#fff' }}>{getExpensePaidByLabel(reverseConfirmDialog.payable) === 'CLIENT PAID' ? 'Send to Stage 1' : 'Send to Stage 3'}</h3>

                         </div>

                         <div className="dialog-body" style={{ padding: '20px' }}>

                             <p style={{ margin: 0, fontSize: '1rem' }}>

                                Are you sure you would like to send this payment back to {getExpensePaidByLabel(reverseConfirmDialog.payable) === 'CLIENT PAID' ? 'Stage 1' : 'Stage 3'}?

                            </p>

                         </div>

                         <div className="dialog-footer">

                             <button

                                 className="dialog-btn dialog-btn-primary"

                                 onClick={() => (getExpensePaidByLabel(reverseConfirmDialog.payable) === 'CLIENT PAID' ? handleReverseToStage1(reverseConfirmDialog.payable) : handleReverseToStage3(reverseConfirmDialog.payable))}

                                 disabled={actionLoading}

                             >

                                 Yes

                             </button>

                             <button

                                 className="dialog-btn dialog-btn-secondary"

                                 onClick={() => setReverseConfirmDialog({ show: false, payable: null })}

                                 disabled={actionLoading}

                             >

                                 No

                             </button>

                         </div>

                     </div>

                 </div>

             )}



             {/* Rejection History Modal */}

             {viewingRejections && (

                <div className="modal-overlay">

                    <div className="dialog-container" style={{ width: '800px', maxWidth: '95%' }}>

                        <div className="dialog-header">

                            <h3>Rejection History - {viewingRejections.invoice_no}</h3>

                            <button className="icon-btn" onClick={() => setViewingRejections(null)} title="Close">

                                <i className="fa fa-times"></i>

                            </button>

                        </div>

                        <div className="dialog-body">

                            <div style={{ overflowX: 'auto' }}>

                                <table className="table table-bordered table-striped">

                                <thead>

                                    <tr>

                                        <th>Date/Time</th>

                                        <th>Rejected By</th>

                                        <th>Stage</th>

                                        <th>Reason</th>

                                    </tr>

                                </thead>

                                <tbody>

                                    {viewingRejections.rejections && viewingRejections.rejections.length > 0 ? (

                                        viewingRejections.rejections.map((r) => (

                                            <tr key={r.id}>

                                                <td>{new Date(r.rejection_date).toLocaleString('en-GB', { day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit' })}</td>

                                                <td>{r.rejected_by_name.toUpperCase()}</td>

                                                <td>{r.stage.toUpperCase()}</td>

                                                <td style={{color: 'red'}}>{r.reason.toUpperCase()}</td>

                                            </tr>

                                        ))

                                    ) : (

                                        <tr><td colSpan="4" className="text-center">No history found</td></tr>

                                    )}

                                </tbody>

                                </table>

                            </div>

                        </div>

                        <div className="dialog-footer">

                            <button className="dialog-btn dialog-btn-secondary" onClick={() => setViewingRejections(null)}>Close</button>

                        </div>

                    </div>

                </div>

             )}





                 {/* Stage 2 vendor mismatch dialog */}

                 {showStage2VendorDialog && (

                    <div className="modal-overlay">

                        <div className="dialog-container" style={{ width: '520px', maxWidth: '95%' }}>

                            <div className="dialog-header" style={{ background: '#0d6efd', color: '#fff', borderRadius: '4px 4px 0 0' }}>

                                <h3 style={{ margin: 0, color: '#fff' }}>You can only select Payables of the same Vendor</h3>

                            </div>

                            <div className="dialog-body" style={{ padding: '20px' }}>

                                <p style={{ margin: 0, fontSize: '1rem' }}>

                                    You can only select payables for the same vendor in one batch.

                                    Currently selected vendor: <strong>{selectedStage2VendorName}</strong>.

                                </p>

                            </div>

                            <div className="dialog-footer">

                                <button className="dialog-btn dialog-btn-primary" onClick={() => setShowStage2VendorDialog(false)}>

                                    OK

                                </button>

                            </div>

                        </div>

                    </div>

                 )}



                 {/* Stage 3 vendor mismatch dialog */}

                 {showStage3VendorDialog && (

                    <div className="modal-overlay">

                        <div className="dialog-container" style={{ width: '520px', maxWidth: '95%' }}>

                            <div className="dialog-header" style={{ background: '#0d6efd', color: '#fff', borderRadius: '4px 4px 0 0' }}>

                                <h3 style={{ margin: 0, color: '#fff' }}>You can only select Payables of the same Vendor</h3>

                            </div>

                            <div className="dialog-body" style={{ padding: '20px' }}>

                                <p style={{ margin: 0, fontSize: '1rem' }}>

                                    You can only select payables for the same vendor in one batch.

                                    Currently selected vendor: <strong>{selectedStage3VendorName}</strong>.

                                </p>

                            </div>

                            <div className="dialog-footer">

                                <button className="dialog-btn dialog-btn-primary" onClick={() => setShowStage3VendorDialog(false)}>

                                    OK

                                </button>

                            </div>

                        </div>

                    </div>

                 )}



                 {/* Stage 1 vendor mismatch dialog */}

                 {showStage1VendorDialog && (

                    <div className="modal-overlay">

                        <div className="dialog-container" style={{ width: '520px', maxWidth: '95%' }}>

                            <div className="dialog-header" style={{ background: '#0d6efd', color: '#fff', borderRadius: '4px 4px 0 0' }}>

                                <h3 style={{ margin: 0, color: '#fff' }}>You can only select Payables of the same Vendor</h3>

                            </div>

                            <div className="dialog-body" style={{ padding: '20px' }}>

                                <p style={{ margin: 0, fontSize: '1rem' }}>

                                    You can only select payables for the same vendor in one batch.

                                    Currently selected vendor: <strong>{selectedStage1VendorName}</strong>.

                                </p>

                            </div>

                            <div className="dialog-footer">

                                <button className="dialog-btn dialog-btn-primary" onClick={() => setShowStage1VendorDialog(false)}>

                                    OK

                                </button>

                            </div>

                        </div>

                    </div>

                 )}



                 {/* New Payable Form */}

                 {showNewPayableForm && (

                     <PopoutWindow

                         title="New Payable"

                         name="new_payable_form"

                         toastContainerId={PAYABLE_POPOUT_TOAST_ID}

                         onSave={handleFormSuccess}

                         onClose={() => setShowNewPayableForm(false)}

                     >

                         <OtherPayablesForm

                             mode="add"

                             receiptUploadOnly={false}

                             initialData={null}

                             onClose={() => setShowNewPayableForm(false)}

                             onSave={() => { setShowNewPayableForm(false); fetchCounts(); fetchPayables(); }}

                             module="ACCOUNTS"

                             popoutMode={true}

                             toastContainerId={PAYABLE_POPOUT_TOAST_ID}

                            

                         />

                     </PopoutWindow>

                 )}



                 {/* Stage 2 Bulk Pay form in popup */}

                 {showBulkPayForm && selectedStage2.length > 0 && (

                     <PopoutWindow

                         title={`Bulk Pay — Stage 2 — ${selectedStage2VendorName} (${selectedStage2.length} payables)`}

                         name="bulk_pay_stage2"

                         toastContainerId={PAYABLE_POPOUT_TOAST_ID}

                            onSave={handleFormSuccess}

                         onClose={() => setShowBulkPayForm(false)}

                     >

                         <BulkPayForm

                             payables={payables.filter(p => selectedStage2.includes(p.reference_no))}

                             onClose={() => setShowBulkPayForm(false)}

                             onSuccess={() => {

                                 setShowBulkPayForm(false);

                                 setSelectedStage2([]);

                                 fetchCounts();

                                 fetchPayables();

                             }}

                             toastContainerId={PAYABLE_POPOUT_TOAST_ID}

                            onSave={handleFormSuccess}

                             popoutMode={true}

                         />

                     </PopoutWindow>

                 )}



                 {/* Stage 3 Bulk Approval form in popup */}

                 {showBulkApprovalForm && selectedStage3.length > 0 && (

                     <PopoutWindow

                         title={`Bulk Approval — Stage 3 — ${selectedStage3VendorName} (${selectedStage3.length} payables)`}

                         name="bulk_approval_stage3"

                         toastContainerId={PAYABLE_POPOUT_TOAST_ID}

                            onSave={handleFormSuccess}

                         onClose={() => setShowBulkApprovalForm(false)}

                     >

                         <BulkPayForm

                             payables={payables.filter(p => selectedStage3.includes(p.reference_no))}

                             onClose={() => setShowBulkApprovalForm(false)}

                             onSuccess={() => {

                                 setShowBulkApprovalForm(false);

                                 setSelectedStage3([]);

                                 fetchCounts();

                                 fetchPayables();

                             }}

                             toastContainerId={PAYABLE_POPOUT_TOAST_ID}

                            onSave={handleFormSuccess}

                             popoutMode={true}

                             mode="approval"

                         />

                     </PopoutWindow>

                 )}



                 {/* Linked Stage 2/3 Bulk Group form opened from a single row */}

                 {showBulkGroupForm && bulkGroupPayables.length > 0 && (

                     <PopoutWindow

                         title={`Bulk Approval — Stage 3 — Linked Group (${bulkGroupPayables.length} payables)`}

                         name="bulk_group_stage3"

                         toastContainerId={PAYABLE_POPOUT_TOAST_ID}

                         onSave={handleFormSuccess}

                         onClose={() => setShowBulkGroupForm(false)}

                     >

                         <BulkPayForm

                             payables={bulkGroupPayables}

                             onClose={() => setShowBulkGroupForm(false)}

                             onSuccess={() => {

                                 setShowBulkGroupForm(false);

                                 setBulkGroupPayables([]);

                                 fetchCounts();

                                 fetchPayables();

                             }}

                             toastContainerId={PAYABLE_POPOUT_TOAST_ID}

                             onSave={handleFormSuccess}

                             popoutMode={true}

                             mode="approval"

                         />

                     </PopoutWindow>

                 )}



             {showTasacModal && (

                <div className="modal-overlay">

                    <div className="dialog-container" style={{ width: '650px', maxWidth: '95%' }}>

                        <div className="dialog-header" style={{ background: '#0d6efd', color: '#fff', borderRadius: '4px 4px 0 0' }}>

                            <h3 style={{ margin: 0, color: '#fff' }}>Tasac Fees Document</h3>

                            <button className="icon-btn" onClick={() => setShowTasacModal(false)} title="Close" style={{ color: '#fff' }}>

                                <i className="fa fa-times"></i>

                            </button>

                        </div>

                        <div className="dialog-body" style={{ padding: '20px' }}>

                            {tasacLoading ? (

                                <div>Loading document details...</div>

                            ) : (

                                <>

                                    <div style={{ marginBottom: '15px', padding: '12px', background: '#f8f9fa', border: '1px solid #e9ecef', borderRadius: '4px' }}>

                                        <div style={{ fontWeight: 'bold', marginBottom: '8px' }}>Current Tasac Fees Document</div>

                                        {tasacDocInfo?.file_url ? (

                                            <div style={{ display: 'flex', alignItems: 'center', gap: '10px', flexWrap: 'wrap' }}>

                                                <span style={{ color: '#555' }}>{tasacDocInfo.file_name || 'Tasac Fees Document'}</span>

                                                <button

                                                    type="button"

                                                    className="btn btn-sm btn-info"

                                                    onClick={() => openFilePreview(getTasacDocumentLink(tasacDocInfo), tasacDocInfo.file_name || 'Tasac Fees Document')}

                                                >

                                                    <i className="fa fa-eye"></i> View

                                                </button>

                                            </div>

                                        ) : (

                                            <span className="text-muted">No Tasac Fees document uploaded yet.</span>

                                        )}

                                        {tasacDocInfo?.updated_at && (

                                            <div style={{ marginTop: '8px', color: '#666', fontSize: '0.9em' }}>

                                                Updated: {new Date(tasacDocInfo.updated_at).toLocaleString('en-GB', { day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit' })}

                                                {tasacDocInfo.uploaded_by ? ` by ${tasacDocInfo.uploaded_by}` : ''}

                                            </div>

                                        )}

                                    </div>



                                    <div>

                                        <label style={{ fontWeight: 'bold' }}>{tasacDocInfo?.file_url ? 'Replace Document' : 'Upload Document'}</label>

                                        <input

                                            type="file"

                                            className="form-control-file"

                                            onChange={(e) => setTasacFile(e.target.files?.[0] || null)}

                                            style={{ marginTop: '8px' }}

                                        />

                                        {tasacFile && (

                                            <div style={{ marginTop: '8px', color: '#555' }}>Selected: {tasacFile.name}</div>

                                        )}

                                    </div>

                                </>

                            )}

                        </div>

                        <div className="dialog-footer">

                            <button className="dialog-btn dialog-btn-secondary" onClick={() => setShowTasacModal(false)} disabled={tasacSubmitting}>

                                Close

                            </button>

                            <button className="dialog-btn dialog-btn-primary" onClick={handleTasacUpload} disabled={tasacSubmitting || !tasacFile}>

                                {tasacSubmitting ? 'Saving...' : (tasacDocInfo?.file_url ? 'Replace' : 'Upload')}

                            </button>

                        </div>

                    </div>

                </div>

             )}

        {previewOpen && (

            <PopoutWindow

                name="InvoicePreview"

                title="View Invoice"

                width={1100}

                height={800}

                onClose={closeInvoicePreview}

                toastContainerId="InvoicePreviewToasts"

            >

                <InvoicePreviewDialog

                    key={previewJobId}

                    jobId={previewJobId}

                    shipmentType={previewShipmentType}

                    isPending={false}

                    canCreate={false}

                    onClose={closeInvoicePreview}

                    onGenerated={closeInvoicePreview}

                />

            </PopoutWindow>

        )}

        </div>

    );

};



export default Payables;







